// Turbomachinery in Fluid Dynamics

const S = {
  wrapper: { marginTop:'10px', padding:'40px', border:'3px solid grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1: { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c0392b', paddingBottom:'8px', marginBottom:'24px', color:'#7B0D0D' },
  h2: { fontSize:'1.3rem', fontWeight:'700', color:'#c0392b', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c0392b', paddingLeft:'10px' },
  p: { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox: { background:'#FEF0F0', border:'2px solid #c0392b', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#7B0D0D', marginBottom:'8px' },
  infoBoxItem: { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c0392b' },
  table: { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th: { background:'#c0392b', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE: { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff5f5' },
  tdO: { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff' },
  tdL: { fontWeight:'600', color:'#7B0D0D', whiteSpace:'nowrap', width:'220px' },
  code: { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap: { margin:'24px 0', background:'#fffafa', border:'1px solid #f0cece', borderRadius:'8px', padding:'20px' },
  dTitle: { fontWeight:'700', fontSize:'0.95rem', color:'#7B0D0D', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption: { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Explanation / Formula</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Diagram 1: Turbomachinery Classification ──────────────────────
function DiagramClassification() {
  const W = 900, H = 310;

  const branches = [
    // Work-absorbing (left)
    { x:50,  y:80,  w:160, h:44, fill:'#FEF0F0', stroke:'#c0392b', label:'Compressors', sub:'raise gas pressure' },
    { x:50,  y:150, w:160, h:44, fill:'#FEF0F0', stroke:'#c0392b', label:'Fans / Blowers', sub:'low Δp gas movers' },
    { x:50,  y:220, w:160, h:44, fill:'#FEF0F0', stroke:'#c0392b', label:'Pumps', sub:'raise liquid pressure' },
    // Work-producing (right)
    { x:690, y:80,  w:160, h:44, fill:'#e8f4e8', stroke:'#27ae60', label:'Gas Turbines', sub:'expand hot gas' },
    { x:690, y:150, w:160, h:44, fill:'#e8f4e8', stroke:'#27ae60', label:'Steam Turbines', sub:'expand steam' },
    { x:690, y:220, w:160, h:44, fill:'#e8f4e8', stroke:'#27ae60', label:'Hydraulic Turbines', sub:'extract water energy' },
  ];

  // Flow types
  const flowTypes = [
    { x:230, y:80,  w:140, h:44, fill:'#fff5e6', stroke:'#e07b00', label:'Axial flow', sub:'fluid ∥ shaft axis' },
    { x:230, y:150, w:140, h:44, fill:'#fff5e6', stroke:'#e07b00', label:'Mixed flow', sub:'axial + radial' },
    { x:230, y:220, w:140, h:44, fill:'#fff5e6', stroke:'#e07b00', label:'Radial flow', sub:'fluid ⊥ shaft (centrifugal)' },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Turbomachinery Classification — Work Direction and Flow Geometry</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="clArr" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#555"/>
          </marker>
          <marker id="clArrR" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#c0392b"/>
          </marker>
          <marker id="clArrG" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#27ae60"/>
          </marker>
        </defs>

        {/* Central rotor circle */}
        <circle cx={450} cy={155} r={72} fill="#fff5f5" stroke="#c0392b" strokeWidth="2.5"/>
        <text x={450} y={148} textAnchor="middle" fontSize="15" fill="#7B0D0D" fontWeight="700">ROTOR</text>
        <text x={450} y={166} textAnchor="middle" fontSize="12" fill="#7B0D0D">(rotating element)</text>

        {/* Work-absorbing label */}
        <rect x={10} y={14} width={195} height={32} rx={4} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5"/>
        <text x={107} y={34} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Work-Absorbing (Machines)</text>

        {/* Work-producing label */}
        <rect x={695} y={14} width={195} height={32} rx={4} fill="#e8f4e8" stroke="#27ae60" strokeWidth="1.5"/>
        <text x={792} y={34} textAnchor="middle" fontSize="13" fill="#145214" fontWeight="700">Work-Producing (Engines)</text>

        {/* Work-absorbing boxes */}
        {branches.filter((_,i)=>i<3).map(({x,y,w,h,fill,stroke,label,sub}) => (
          <g key={label}>
            <rect x={x} y={y} width={w} height={h} rx={4} fill={fill} stroke={stroke} strokeWidth="1.5"/>
            <text x={x+w/2} y={y+17} textAnchor="middle" fontSize="12" fill="#7B0D0D" fontWeight="700">{label}</text>
            <text x={x+w/2} y={y+32} textAnchor="middle" fontSize="10" fill="#555">{sub}</text>
            {/* Arrow from box to rotor */}
            <line x1={x+w} y1={y+h/2} x2={375} y2={155+(y-150)*0.3}
              stroke="#c0392b" strokeWidth="1.5" strokeDasharray="5,3" markerEnd="url(#clArrR)"/>
          </g>
        ))}

        {/* Flow type boxes (centre-left) */}
        {flowTypes.map(({x,y,w,h,fill,stroke,label,sub}) => (
          <g key={label}>
            <rect x={x} y={y} width={w} height={h} rx={4} fill={fill} stroke={stroke} strokeWidth="1.5"/>
            <text x={x+w/2} y={y+17} textAnchor="middle" fontSize="12" fill="#8B4513" fontWeight="700">{label}</text>
            <text x={x+w/2} y={y+32} textAnchor="middle" fontSize="10" fill="#555">{sub}</text>
          </g>
        ))}

        {/* Flow type label */}
        <rect x={230} y={14} width={140} height={32} rx={4} fill="#fff5e6" stroke="#e07b00" strokeWidth="1.5"/>
        <text x={300} y={34} textAnchor="middle" fontSize="13" fill="#8B4513" fontWeight="700">Flow Direction</text>

        {/* Work-producing boxes */}
        {branches.filter((_,i)=>i>=3).map(({x,y,w,h,fill,stroke,label,sub}) => (
          <g key={label}>
            <rect x={x} y={y} width={w} height={h} rx={4} fill={fill} stroke={stroke} strokeWidth="1.5"/>
            <text x={x+w/2} y={y+17} textAnchor="middle" fontSize="13" fill="#145214" fontWeight="700">{label}</text>
            <text x={x+w/2} y={y+32} textAnchor="middle" fontSize="10" fill="#555">{sub}</text>
            <line x1={525} y1={155+(y-150)*0.3} x2={x} y2={y+h/2}
              stroke="#27ae60" strokeWidth="1.5" strokeDasharray="5,3" markerEnd="url(#clArrG)"/>
          </g>
        ))}

        {/* Shaft arrow through rotor */}
        <line x1={370} y1={155} x2={530} y2={155} stroke="#555" strokeWidth="3"/>
        <text x={450} y={245} textAnchor="middle" fontSize="12" fill="#555">Shaft</text>

        {/* Energy arrows */}
        <line x1={10} y1={280} x2={375} y2={200} stroke="#c0392b" strokeWidth="2.5" markerEnd="url(#clArrR)"/>
        <text x={140} y={295} textAnchor="middle" fontSize="12" fill="#c0392b" fontWeight="700">↑ Energy input (W_in)</text>
        <line x1={525} y1={200} x2={890} y2={280} stroke="#27ae60" strokeWidth="2.5" markerEnd="url(#clArrG)"/>
        <text x={760} y={295} textAnchor="middle" fontSize="12" fill="#27ae60" fontWeight="700">Energy output (W_out) ↓</text>

        {/* Specific speed annotation */}
        <rect x={390} y={240} width={120} height={30} rx={3} fill="#f5e6ff" stroke="#9b59b6" strokeWidth="1"/>
        <text x={450} y={257} textAnchor="middle" fontSize="10" fill="#5b2c8d" fontWeight="700">Specific speed Ns</text>
        <text x={450} y={268} textAnchor="middle" fontSize="10" fill="#5b2c8d">selects flow type</text>
      </svg>
      <div style={S.dCaption}>Turbomachinery divides into work-absorbing machines (compressors, fans, pumps) that add energy to a fluid via a rotating impeller, and work-producing machines (turbines) that extract energy from a moving fluid through a rotor. A further classification by flow geometry — axial, mixed, or radial (centrifugal) — is determined primarily by the specific speed Ns, which ranks designs by their combination of head, flow rate, and rotational speed. All turbomachines share the same governing principle: angular momentum change via the Euler turbomachine equation.</div>
    </div>
  );
}

// ── Diagram 2: Euler Turbomachine Equation — Velocity Triangles ──
function DiagramVelocityTriangles() {
  const W = 900, H = 380;

  // ── Helper: draw one clean velocity triangle ──────────────────
  // Origin A is the left vertex (tail of U).
  // U goes horizontally right to B.
  // C goes from A up-right to apex D.
  // W goes from B to D (closes the triangle).
  // All labels placed well clear of vectors.
  //
  // Inlet (station 1): axial entry, Cu1 = 0
  //   A=(60,190), U1=160 → B=(220,190), Ca1=100 up → D=(60,90)
  //   C1: A→D  (pure axial, Cu1=0)
  //   W1: B→D
  //
  // Outlet (station 2): Cu2 > 0 (pump — work added)
  //   A=(510,190), U2=200 → B=(710,190), Ca2=100 up, Cu2=130 → D=(510+130,90)=(640,90)
  //   C2: A→D  (tilted — has whirl Cu2)
  //   W2: B→D

  // Inlet triangle vertices
  const A1 = {x:60,  y:190};
  const B1 = {x:230, y:190};  // U1 = 170 px long
  const D1 = {x:60,  y:82};   // Ca1 = 108 px up; Cu1 = 0 (pure axial)

  // Outlet triangle vertices
  const A2 = {x:490, y:190};
  const B2 = {x:700, y:190};  // U2 = 210 px long
  const D2 = {x:640, y:82};   // Ca2 ≈ 108, Cu2 = 150 px rightward

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Euler Turbomachine Equation — Velocity Triangles at Inlet and Outlet</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="vtU"  markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
            <polygon points="0 0, 7 4, 0 8" fill="#c0392b"/>
          </marker>
          <marker id="vtC"  markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
            <polygon points="0 0, 7 4, 0 8" fill="#27ae60"/>
          </marker>
          <marker id="vtW"  markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
            <polygon points="0 0, 7 4, 0 8" fill="#2980b9"/>
          </marker>
          <marker id="vtDim" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
            <polygon points="0 0, 5 3, 0 6" fill="#888"/>
          </marker>
        </defs>

        {/* ════════════════ LEFT PANEL — Inlet ════════════════ */}
        <rect x={10} y={10} width={425} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={222} y={30} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Inlet  (Station 1) — Axial Entry, Cu₁ = 0</text>

        {/* ── Triangle ── */}
        {/* U1: A1 → B1  (blade speed, horizontal red) */}
        <line x1={A1.x} y1={A1.y} x2={B1.x} y2={B1.y}
          stroke="#c0392b" strokeWidth="3" markerEnd="url(#vtU)"/>
        {/* C1: A1 → D1  (absolute velocity, green) */}
        <line x1={A1.x} y1={A1.y} x2={D1.x} y2={D1.y}
          stroke="#27ae60" strokeWidth="3" markerEnd="url(#vtC)"/>
        {/* W1: B1 → D1  (relative velocity, blue) */}
        <line x1={B1.x} y1={B1.y} x2={D1.x} y2={D1.y}
          stroke="#2980b9" strokeWidth="3" markerEnd="url(#vtW)"/>

        {/* ── Dashed component lines from D1 ── */}
        {/* Ca1: vertical drop from D1 to baseline */}
        <line x1={D1.x} y1={D1.y} x2={D1.x} y2={A1.y}
          stroke="#888" strokeWidth="1.4" strokeDasharray="5,3"/>
        {/* Cu1 = 0: just a dot, no horizontal component */}

        {/* ── Angle arc α1 at A1 (between U1 and C1) ── */}
        {/* C1 is straight up from A1, so α1 = 90° — mark the right-angle box */}
        <rect x={A1.x+2} y={A1.y-14} width={12} height={12}
          fill="none" stroke="#e07b00" strokeWidth="1.5"/>
        <text x={A1.x+18} y={A1.y-4} fontSize="12" fill="#e07b00" fontWeight="700">α₁=90°</text>
        <text x={A1.x+18} y={A1.y+10} fontSize="10" fill="#888">(no inlet whirl)</text>

        {/* β1 arc at B1 (angle of W1 from horizontal) */}
        {/* W1 goes left and up from B1: angle = atan2(D1.y-B1.y, D1.x-B1.x) */}
        <path d={`M ${B1.x-32},${B1.y} A 32 32 0 0 0 ${B1.x + 32*Math.cos(Math.atan2(D1.y-B1.y, D1.x-B1.x))},${B1.y + 32*Math.sin(Math.atan2(D1.y-B1.y, D1.x-B1.x))}`}
          fill="none" stroke="#9b59b6" strokeWidth="1.8"/>
        <text x={B1.x-50} y={B1.y-26} textAnchor="middle" fontSize="12" fill="#9b59b6" fontWeight="700">β₁</text>
        <text x={B1.x-50} y={B1.y-12} textAnchor="middle" fontSize="10" fill="#9b59b6">blade angle</text>

        {/* ── Vector labels — placed well clear ── */}
        {/* U1 label: below the vector, centred */}
        <text x={(A1.x+B1.x)/2} y={A1.y+20} textAnchor="middle" fontSize="13" fill="#c0392b" fontWeight="700">U₁</text>
        <text x={(A1.x+B1.x)/2} y={A1.y+34} textAnchor="middle" fontSize="11" fill="#c0392b">blade speed = ω r₁</text>

        {/* C1 label: left of the vector */}
        <text x={A1.x-10} y={(A1.y+D1.y)/2+4} textAnchor="end" fontSize="13" fill="#27ae60" fontWeight="700">C₁</text>
        <text x={A1.x-10} y={(A1.y+D1.y)/2+18} textAnchor="end" fontSize="11" fill="#27ae60">absolute</text>

        {/* W1 label: right of the W1 vector, above midpoint */}
        <text x={(B1.x+D1.x)/2+12} y={(B1.y+D1.y)/2-6} fontSize="13" fill="#2980b9" fontWeight="700">W₁</text>
        <text x={(B1.x+D1.x)/2+12} y={(B1.y+D1.y)/2+8} fontSize="11" fill="#2980b9">relative</text>

        {/* Ca1 dimension label */}
        <text x={D1.x-12} y={(D1.y+A1.y)/2+4} textAnchor="end" fontSize="11" fill="#888">Ca₁</text>

        {/* Cu1 = 0 annotation at apex */}
        <circle cx={D1.x} cy={D1.y} r={5} fill="#e07b00" stroke="#fff" strokeWidth="1.5"/>
        <text x={D1.x+10} y={D1.y-10} fontSize="11" fill="#e07b00" fontWeight="700">Cu₁ = 0</text>
        <text x={D1.x+10} y={D1.y+4} fontSize="10" fill="#888">(apex on axial line)</text>

        {/* ── Info box ── */}
        <rect x={18} y={236} width={408} height={108} rx={4} fill="#fff5f5" stroke="#f0cece"/>
        <text x={26} y={253} fontSize="12" fill="#7B0D0D" fontWeight="700">Velocity triangle relation:  C⃗ = U⃗ + W⃗</text>
        <text x={26} y={270} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">Axial:  Ca = C cosα  =  W cosβ</text>
        <text x={26} y={286} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">Whirl:  Cu = C sinα  =  U − W sinβ</text>
        <text x={26} y={304} fontSize="12" fill="#c0392b" fontWeight="700">Axial entry (Cu₁=0): α₁=90°, C₁=Ca₁=W₁cosβ₁</text>
        <text x={26} y={320} fontSize="11" fill="#555">Ideal entry: W₁ aligned with blade angle β₁ — zero incidence losses</text>
        <text x={26} y={336} fontSize="11" fill="#555">Euler work (axial entry): W = U₂Cu₂  (since Cu₁ = 0)</text>

        {/* ════════════════ RIGHT PANEL — Outlet ════════════════ */}
        <rect x={445} y={10} width={445} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={667} y={30} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Outlet  (Station 2) — Work Added, Cu₂ &gt; 0</text>

        {/* ── Triangle ── */}
        {/* U2: A2 → B2 */}
        <line x1={A2.x} y1={A2.y} x2={B2.x} y2={B2.y}
          stroke="#c0392b" strokeWidth="3" markerEnd="url(#vtU)"/>
        {/* C2: A2 → D2 */}
        <line x1={A2.x} y1={A2.y} x2={D2.x} y2={D2.y}
          stroke="#27ae60" strokeWidth="3" markerEnd="url(#vtC)"/>
        {/* W2: B2 → D2 */}
        <line x1={B2.x} y1={B2.y} x2={D2.x} y2={D2.y}
          stroke="#2980b9" strokeWidth="3" markerEnd="url(#vtW)"/>

        {/* ── Dashed component lines from D2 ── */}
        {/* Ca2: vertical drop from D2 to baseline */}
        <line x1={D2.x} y1={D2.y} x2={D2.x} y2={A2.y}
          stroke="#888" strokeWidth="1.4" strokeDasharray="5,3"/>
        {/* Cu2: horizontal from A2 to foot of Ca2 */}
        <line x1={A2.x} y1={A2.y} x2={D2.x} y2={A2.y}
          stroke="#e07b00" strokeWidth="1.8" strokeDasharray="5,3"/>

        {/* ── Angle arc α2 at A2 (between baseline and C2) ── */}
        {/* C2 angle from horizontal: atan2(D2.y-A2.y, D2.x-A2.x) */}
        {(()=>{
          const ang = Math.atan2(D2.y - A2.y, D2.x - A2.x); // negative (upward)
          const r = 36;
          return <path d={`M ${A2.x+r},${A2.y} A ${r} ${r} 0 0 0 ${A2.x + r*Math.cos(ang)},${A2.y + r*Math.sin(ang)}`}
            fill="none" stroke="#e07b00" strokeWidth="1.8"/>;
        })()}
        <text x={A2.x+44} y={A2.y-20} fontSize="13" fill="#e07b00" fontWeight="700">α₂</text>
        <text x={A2.x+44} y={A2.y-6}  fontSize="10" fill="#888">abs. flow angle</text>

        {/* β2 arc at B2 */}
        {(()=>{
          const ang = Math.atan2(D2.y - B2.y, D2.x - B2.x);
          const r = 34;
          return <path d={`M ${B2.x-r},${B2.y} A ${r} ${r} 0 0 0 ${B2.x + r*Math.cos(ang + Math.PI)},${B2.y + r*Math.sin(ang + Math.PI)}`}
            fill="none" stroke="#9b59b6" strokeWidth="1.8"/>;
        })()}
        <text x={B2.x-52} y={B2.y-26} textAnchor="middle" fontSize="13" fill="#9b59b6" fontWeight="700">β₂</text>
        <text x={B2.x-52} y={B2.y-12} textAnchor="middle" fontSize="10" fill="#9b59b6">blade angle</text>

        {/* ── Vector labels ── */}
        <text x={(A2.x+B2.x)/2} y={A2.y+20} textAnchor="middle" fontSize="13" fill="#c0392b" fontWeight="700">U₂</text>
        <text x={(A2.x+B2.x)/2} y={A2.y+34} textAnchor="middle" fontSize="11" fill="#c0392b">blade speed = ω r₂  &gt; U₁</text>

        {/* C2 label: left of vector */}
        <text x={A2.x-10} y={(A2.y+D2.y)/2+4} textAnchor="end" fontSize="13" fill="#27ae60" fontWeight="700">C₂</text>
        <text x={A2.x-10} y={(A2.y+D2.y)/2+18} textAnchor="end" fontSize="11" fill="#27ae60">absolute</text>

        {/* W2 label: right side of W2 vector */}
        <text x={(B2.x+D2.x)/2+12} y={(B2.y+D2.y)/2-4} fontSize="13" fill="#2980b9" fontWeight="700">W₂</text>
        <text x={(B2.x+D2.x)/2+12} y={(B2.y+D2.y)/2+10} fontSize="11" fill="#2980b9">relative</text>

        {/* Ca2 and Cu2 dimension labels */}
        <text x={D2.x+8} y={(D2.y+A2.y)/2+4} fontSize="11" fill="#888">Ca₂</text>
        <text x={(A2.x+D2.x)/2} y={A2.y+14} textAnchor="middle" fontSize="11" fill="#e07b00" fontWeight="700">Cu₂</text>

        {/* Apex dot */}
        <circle cx={D2.x} cy={D2.y} r={5} fill="#27ae60" stroke="#fff" strokeWidth="1.5"/>

        {/* ── Euler equation box ── */}
        <rect x={453} y={236} width={430} height={108} rx={4} fill="#e8f4e8" stroke="#27ae60" strokeWidth="1.5"/>
        <text x={461} y={253} fontSize="12" fill="#145214" fontWeight="700">Euler Turbomachine Equation:</text>
        <text x={461} y={272} fontSize="14" fill="#145214" fontFamily="'Courier New',monospace" fontWeight="700">W = U₂Cu₂ − U₁Cu₁</text>
        <text x={461} y={289} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">  = ω(r₂Cu₂ − r₁Cu₁)   (specific work, J/kg)</text>
        <text x={461} y={307} fontSize="11" fill="#555">Pump/compressor:  Cu₂ &gt; Cu₁  →  W &gt; 0  (work in)</text>
        <text x={461} y={322} fontSize="11" fill="#555">Turbine:  Cu₂ &lt; Cu₁  →  W &gt; 0  (work out)</text>
        <text x={461} y={338} fontSize="11" fill="#7B0D0D" fontWeight="700">Axial entry (Cu₁=0):  W = U₂Cu₂  (simplest case)</text>

        {/* ── Colour legend ── */}
        <line x1={455} y1={57} x2={485} y2={57} stroke="#c0392b" strokeWidth="3" markerEnd="url(#vtU)"/>
        <text x={490} y={61} fontSize="11" fill="#c0392b" fontWeight="700">U — blade speed (= ω r)</text>
        <line x1={455} y1={76} x2={485} y2={76} stroke="#27ae60" strokeWidth="3" markerEnd="url(#vtC)"/>
        <text x={490} y={80} fontSize="11" fill="#27ae60" fontWeight="700">C — absolute velocity</text>
        <line x1={455} y1={95} x2={485} y2={95} stroke="#2980b9" strokeWidth="3" markerEnd="url(#vtW)"/>
        <text x={490} y={99} fontSize="11" fill="#2980b9" fontWeight="700">W — relative velocity  (C = U + W)</text>
        <line x1={455} y1={114} x2={485} y2={114} stroke="#e07b00" strokeWidth="1.8" strokeDasharray="5,3"/>
        <text x={490} y={118} fontSize="11" fill="#e07b00">Cu — whirl (tangential) component</text>
        <line x1={455} y1={130} x2={485} y2={130} stroke="#888" strokeWidth="1.4" strokeDasharray="5,3"/>
        <text x={490} y={134} fontSize="11" fill="#888">Ca — axial (meridional) component</text>
      </svg>
      <div style={S.dCaption}>The velocity triangle constructs the three velocity vectors at any blade row: absolute velocity C (seen by a stationary observer), blade speed U = ωr, and relative velocity W = C − U (seen by a rider on the blade). At inlet, pumps and compressors are designed for axial entry (Cu₁ = 0, α₁ = 90°) so no pre-swirl exists and the blade angle β₁ equals the relative flow angle. At outlet, the rotor has deflected the flow, adding whirl Cu₂; this whirl is the source of all shaft work. The Euler equation W = U₂Cu₂ − U₁Cu₁ follows directly from the moment-of-momentum theorem and applies to every turbomachine regardless of fluid type.</div>
    </div>
  );
}

// ── Diagram 3: Centrifugal Pump — Cross-Section & Characteristics ─
function DiagramCentrifugalPump() {
  const W = 900, H = 340;
  const PW = (W - 30) / 2;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Centrifugal Pump — Cross-Section and Performance Characteristics</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="cpArr" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#c0392b"/>
          </marker>
          <marker id="cpArrG" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#27ae60"/>
          </marker>
          <radialGradient id="impGrad" cx="40%" cy="35%">
            <stop offset="0%" stopColor="#e8c4c4"/>
            <stop offset="100%" stopColor="#c0392b" stopOpacity="0.3"/>
          </radialGradient>
        </defs>

        {/* Left: Pump cross-section schematic */}
        <rect x={10} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={10+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Centrifugal Pump — Schematic Cross-Section</text>

        {(()=>{
          const cx=200, cy=175;
          // Volute casing (outer ellipse)
          return <>
            {/* Volute casing */}
            <ellipse cx={cx} cy={cy} rx={120} ry={110} fill="#e8e8e8" stroke="#555" strokeWidth="2.5"/>
            {/* Impeller */}
            <circle cx={cx} cy={cy} r={78} fill="url(#impGrad)" stroke="#c0392b" strokeWidth="2"/>
            {/* Impeller blades — backward curved, 6 blades */}
            {Array.from({length:6}, (_,i) => {
              const angle = i * Math.PI/3;
              const r1=28, r2=75;
              const x1=cx+r1*Math.cos(angle), y1=cy+r1*Math.sin(angle);
              const x2=cx+r2*Math.cos(angle+0.6), y2=cy+r2*Math.sin(angle+0.6);
              return <path key={i} d={`M ${x1},${y1} C ${cx+50*Math.cos(angle+0.2)},${cy+50*Math.sin(angle+0.2)} ${cx+65*Math.cos(angle+0.5)},${cy+65*Math.sin(angle+0.5)} ${x2},${y2}`}
                fill="none" stroke="#7B0D0D" strokeWidth="2.2"/>;
            })}
            {/* Hub */}
            <circle cx={cx} cy={cy} r={22} fill="#aaa" stroke="#555" strokeWidth="1.5"/>
            {/* Shaft */}
            <rect x={cx-8} y={cy+22} width={16} height={80} rx={3} fill="#888" stroke="#555" strokeWidth="1.5"/>
            <text x={cx} y={cy+118} textAnchor="middle" fontSize="11" fill="#555">Shaft</text>

            {/* Inlet arrow — axial into page, shown as arrow from top */}
            <line x1={cx} y1={cy-110} x2={cx} y2={cy-82}
              stroke="#2980b9" strokeWidth="3" markerEnd="url(#cpArr)"/>
            <text x={cx} y={cy-118} textAnchor="middle" fontSize="12" fill="#2980b9" fontWeight="700">Inlet (eye)</text>
            <text x={cx} y={cy-104} textAnchor="middle" fontSize="11" fill="#2980b9">low p, low V</text>

            {/* Discharge tangential */}
            <line x1={cx+120} y1={cy-20} x2={cx+155} y2={cy-65}
              stroke="#27ae60" strokeWidth="3" markerEnd="url(#cpArrG)"/>
            <text x={cx+160} y={cy-72} fontSize="12" fill="#27ae60" fontWeight="700">Discharge</text>
            <text x={cx+160} y={cy-58} fontSize="11" fill="#27ae60">high p, high V</text>

            {/* Rotation arrow */}
            <path d={`M ${cx+85},${cy-10} A 85 85 0 0 1 ${cx+10},${cy+85}`}
              fill="none" stroke="#e07b00" strokeWidth="2" markerEnd="url(#cpArr)"/>
            <text x={cx+80} y={cy+60} fontSize="12" fill="#e07b00" fontWeight="700">ω</text>

            {/* Volute label */}
            <text x={cx-108} y={cy+10} textAnchor="end" fontSize="11" fill="#555">Volute</text>
            <text x={cx-108} y={cy+24} textAnchor="end" fontSize="11" fill="#555">(diffuses flow,</text>
            <text x={cx-108} y={cy+38} textAnchor="end" fontSize="11" fill="#555">raises pressure)</text>

            {/* r1, r2 labels */}
            <line x1={cx} y1={cy} x2={cx+28} y2={cy} stroke="#555" strokeWidth="1" strokeDasharray="3,2"/>
            <text x={cx+14} y={cy-5} textAnchor="middle" fontSize="10" fill="#555">r₁</text>
            <line x1={cx} y1={cy} x2={cx+75*Math.cos(Math.PI/6)} y2={cy-75*Math.sin(Math.PI/6)} stroke="#555" strokeWidth="1" strokeDasharray="3,2"/>
            <text x={cx+40} y={cy-20} fontSize="10" fill="#555">r₂</text>
          </>;
        })()}

        {/* Right: Pump characteristic curves */}
        <rect x={PW+20} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={PW+20+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Pump Performance Curves  (fixed speed N)</text>

        {(()=>{
          const ox=PW+50, oy=30, pw=PW-60, ch=250;
          // H-Q curve (head vs flow): drooping parabola H = H0 - k*Q^2
          const H0=220, kH=0.0018;
          const hqPts=[];
          for(let Q=0;Q<=340;Q+=5){
            const H=H0-kH*Q*Q;
            if(H>0) hqPts.push({Q,H});
          }
          // Efficiency curve: parabola peaking at Q=180
          const effPts=[];
          for(let Q=0;Q<=340;Q+=5){
            const eta=88*Math.exp(-Math.pow((Q-180)/110,2));
            effPts.push({Q,eta});
          }
          // Power curve: rising then slightly drooping
          const pwrPts=[];
          for(let Q=0;Q<=340;Q+=5){
            const P=20+0.35*Q-0.0003*Q*Q;
            pwrPts.push({Q,P});
          }

          const qx=(Q)=>ox+(Q/340)*pw;
          const hy=(H)=>oy+ch-(H/240)*ch;
          const ey=(e)=>oy+ch-(e/100)*ch;
          const py=(P)=>oy+ch-(P/130)*ch;

          const hqPath=hqPts.map(({Q,H},i)=>`${i===0?'M':'L'} ${qx(Q)},${hy(H)}`).join(' ');
          const effPath=effPts.map(({Q,eta},i)=>`${i===0?'M':'L'} ${qx(Q)},${ey(eta)}`).join(' ');
          const pwrPath=pwrPts.map(({Q,P},i)=>`${i===0?'M':'L'} ${qx(Q)},${py(P)}`).join(' ');

          // Best efficiency point Q=180
          const Hbep=H0-kH*180*180;
          const etabep=88;

          return <>
            {/* Axes */}
            <line x1={ox} y1={oy+ch} x2={ox+pw+10} y2={oy+ch} stroke="#333" strokeWidth="1.5"/>
            <line x1={ox} y1={oy} x2={ox} y2={oy+ch+10} stroke="#333" strokeWidth="1.5"/>
            {/* Right axis for efficiency */}
            <line x1={ox+pw} y1={oy} x2={ox+pw} y2={oy+ch+10} stroke="#27ae60" strokeWidth="1.5" opacity="0.5"/>

            <text x={ox+pw/2} y={oy+ch+22} textAnchor="middle" fontSize="12" fill="#333" fontWeight="700">Flow rate Q  →</text>
            <text x={ox-8} y={oy-6} textAnchor="end" fontSize="11" fill="#333">H (m)</text>
            <text x={ox+pw+12} y={oy-6} fontSize="11" fill="#27ae60">η (%)</text>

            {/* Y ticks — Head */}
            {[0,60,120,180,240].map(v=>(
              <g key={v}>
                <line x1={ox-4} y1={hy(v)} x2={ox+4} y2={hy(v)} stroke="#333" strokeWidth="1"/>
                <text x={ox-8} y={hy(v)+4} textAnchor="end" fontSize="10" fill="#555">{v}</text>
              </g>
            ))}
            {/* Y ticks — efficiency (right) */}
            {[0,25,50,75,100].map(v=>(
              <g key={v}>
                <line x1={ox+pw-4} y1={ey(v)} x2={ox+pw+4} y2={ey(v)} stroke="#27ae60" strokeWidth="1" opacity="0.6"/>
                <text x={ox+pw+14} y={ey(v)+4} fontSize="10" fill="#27ae60">{v}</text>
              </g>
            ))}
            {/* X ticks */}
            {[0,100,200,300].map(v=>(
              <g key={v}>
                <line x1={qx(v)} y1={oy+ch} x2={qx(v)} y2={oy+ch+4} stroke="#333" strokeWidth="1"/>
                <text x={qx(v)} y={oy+ch+14} textAnchor="middle" fontSize="10" fill="#555">{v}</text>
              </g>
            ))}

            {/* Curves */}
            <path d={hqPath}  fill="none" stroke="#c0392b" strokeWidth="2.8"/>
            <path d={effPath} fill="none" stroke="#27ae60" strokeWidth="2.5"/>
            <path d={pwrPath} fill="none" stroke="#e07b00" strokeWidth="2" strokeDasharray="6,3"/>

            {/* BEP marker */}
            <circle cx={qx(180)} cy={hy(Hbep)} r={6} fill="#c0392b" stroke="#fff" strokeWidth="1.5"/>
            <circle cx={qx(180)} cy={ey(etabep)} r={6} fill="#27ae60" stroke="#fff" strokeWidth="1.5"/>
            <line x1={qx(180)} y1={hy(Hbep)} x2={qx(180)} y2={ey(etabep)}
              stroke="#e07b00" strokeWidth="1.5" strokeDasharray="4,3"/>
            <line x1={qx(180)} y1={oy+ch} x2={qx(180)} y2={ey(etabep)}
              stroke="#e07b00" strokeWidth="1" strokeDasharray="3,2" opacity="0.5"/>
            <text x={qx(180)+6} y={hy(Hbep)-8} fontSize="12" fill="#e07b00" fontWeight="700">BEP</text>
            <text x={qx(180)+6} y={hy(Hbep)+6} fontSize="10" fill="#e07b00">(Best Efficiency Point)</text>

            {/* System curve */}
            {(()=>{
              const sysPts=[];
              for(let Q=0;Q<=320;Q+=10){
                const Hsys=40+0.0015*Q*Q;
                if(Hsys<=240) sysPts.push({Q,H:Hsys});
              }
              const sysPath=sysPts.map(({Q,H},i)=>`${i===0?'M':'L'} ${qx(Q)},${hy(H)}`).join(' ');
              // Intersection with HQ curve
              return <>
                <path d={sysPath} fill="none" stroke="#9b59b6" strokeWidth="2" strokeDasharray="7,4"/>
                <text x={qx(250)+4} y={hy(40+0.0015*250*250)-6} fontSize="11" fill="#9b59b6" fontWeight="700">System curve</text>
                {/* Operating point */}
                <circle cx={qx(218)} cy={hy(H0-kH*218*218)} r={6} fill="#9b59b6" stroke="#fff" strokeWidth="1.5"/>
                <text x={qx(218)+8} y={hy(H0-kH*218*218)-6} fontSize="11" fill="#9b59b6">Operating point</text>
              </>;
            })()}

            {/* Legend */}
            <line x1={ox+5} y1={oy+15} x2={ox+28} y2={oy+15} stroke="#c0392b" strokeWidth="2.8"/>
            <text x={ox+32} y={oy+19} fontSize="11" fill="#c0392b">H–Q curve (head)</text>
            <line x1={ox+5} y1={oy+32} x2={ox+28} y2={oy+32} stroke="#27ae60" strokeWidth="2.5"/>
            <text x={ox+32} y={oy+36} fontSize="11" fill="#27ae60">η–Q curve (efficiency)</text>
            <line x1={ox+5} y1={oy+49} x2={ox+28} y2={oy+49} stroke="#e07b00" strokeWidth="2" strokeDasharray="6,3"/>
            <text x={ox+32} y={oy+53} fontSize="11" fill="#e07b00">P–Q curve (shaft power)</text>
          </>;
        })()}
      </svg>
      <div style={S.dCaption}>A centrifugal pump accelerates fluid radially outward through a rotating impeller; the kinetic energy is then converted to pressure in the volute casing (a diffuser with increasing cross-section). Performance is characterised by the H–Q curve (head vs flow rate), efficiency curve η–Q, and power curve P–Q at a fixed rotational speed. The Best Efficiency Point (BEP) is the design operating point. The system curve (H_system = static head + resistance losses ∝ Q²) intersects the pump curve at the actual operating point. Pumps should be selected and operated as close to BEP as possible to avoid vibration, cavitation, and bearing overload.</div>
    </div>
  );
}

// ── Diagram 4: Axial Flow Compressor Stage ────────────────────────
function DiagramAxialCompressor() {
  const W = 900, H = 320;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Axial Flow Compressor Stage — Rotor, Stator and Velocity Triangles</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="acArr" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#c0392b"/>
          </marker>
          <marker id="acArrG" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#27ae60"/>
          </marker>
          <marker id="acArrB" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#2980b9"/>
          </marker>
        </defs>

        {/* Annular flow path — cross-section */}
        {(()=>{
          const outerY=50, innerY=110, exitY=270;
          const stageW=560, startX=80;

          // Outer/inner casing lines
          return <>
            <line x1={startX} y1={outerY} x2={startX+stageW} y2={outerY}
              stroke="#555" strokeWidth="3"/>
            <line x1={startX} y1={innerY} x2={startX+stageW} y2={innerY}
              stroke="#555" strokeWidth="2"/>

            {/* Flow labels */}
            <text x={startX-10} y={(outerY+innerY)/2+4} textAnchor="end" fontSize="12" fill="#2980b9" fontWeight="700">FLOW →</text>

            {/* ─── Inlet guide vanes ─── */}
            {[65,75,85,95,105].map((y,i) => (
              <line key={i} x1={startX+30} y1={outerY+2} x2={startX+30+(y-85)*0.3} y2={innerY-2}
                stroke="#888" strokeWidth="4"/>
            ))}
            <text x={startX+30} y={outerY-8} textAnchor="middle" fontSize="11" fill="#555">IGV</text>

            {/* ─── Rotor blades ─── */}
            {[0,1,2,3,4,5].map(i => {
              const bx=startX+130;
              const pitch=12;
              const y_top=outerY+4+i*pitch;
              if(y_top>innerY-4) return null;
              // Cambered airfoil shape approximation
              return <path key={i}
                d={`M ${bx},${y_top} C ${bx+25},${y_top-3} ${bx+40},${y_top+6} ${bx+50},${y_top+pitch*0.7}`}
                fill="none" stroke="#c0392b" strokeWidth="3.5"/>;
            })}
            <text x={startX+155} y={outerY-8} textAnchor="middle" fontSize="11" fill="#c0392b" fontWeight="700">ROTOR</text>
            <rect x={startX+110} y={outerY+2} width={10} height={innerY-outerY-4} rx={2} fill="#c0392b" opacity="0.15"/>
            <rect x={startX+195} y={outerY+2} width={10} height={innerY-outerY-4} rx={2} fill="#c0392b" opacity="0.15"/>

            {/* ─── Stator blades ─── */}
            {[0,1,2,3,4,5].map(i => {
              const bx=startX+280;
              const pitch=12;
              const y_top=outerY+4+i*pitch;
              if(y_top>innerY-4) return null;
              return <path key={i}
                d={`M ${bx},${y_top+pitch*0.5} C ${bx+25},${y_top} ${bx+40},${y_top+6} ${bx+50},${y_top+pitch*0.8}`}
                fill="none" stroke="#2980b9" strokeWidth="3.5"/>;
            })}
            <text x={startX+305} y={outerY-8} textAnchor="middle" fontSize="11" fill="#2980b9" fontWeight="700">STATOR</text>
            <rect x={startX+260} y={outerY+2} width={10} height={innerY-outerY-4} rx={2} fill="#2980b9" opacity="0.15"/>
            <rect x={startX+345} y={outerY+2} width={10} height={innerY-outerY-4} rx={2} fill="#2980b9" opacity="0.15"/>

            {/* ─── 2nd Rotor ─── */}
            {[0,1,2,3,4,5].map(i => {
              const bx=startX+420;
              const pitch=12;
              const y_top=outerY+4+i*pitch;
              if(y_top>innerY-4) return null;
              return <path key={i}
                d={`M ${bx},${y_top} C ${bx+25},${y_top-3} ${bx+40},${y_top+6} ${bx+50},${y_top+pitch*0.7}`}
                fill="none" stroke="#c0392b" strokeWidth="3.5"/>;
            })}
            <text x={startX+445} y={outerY-8} textAnchor="middle" fontSize="11" fill="#c0392b" fontWeight="700">ROTOR</text>
            <text x={startX+445} y={outerY-20} textAnchor="middle" fontSize="11" fill="#555">(next stage)</text>
            <rect x={startX+400} y={outerY+2} width={10} height={innerY-outerY-4} rx={2} fill="#c0392b" opacity="0.15"/>
            <rect x={startX+485} y={outerY+2} width={10} height={innerY-outerY-4} rx={2} fill="#c0392b" opacity="0.15"/>

            {/* Pressure rise arrows */}
            <line x1={startX+40} y1={exitY-20} x2={startX+530} y2={exitY-20}
              stroke="#e07b00" strokeWidth="2.5" markerEnd="url(#acArr)"/>
            <text x={startX+290} y={exitY-4} textAnchor="middle" fontSize="12" fill="#e07b00" fontWeight="700">Pressure rises stage by stage → (p₂/p₁)^N overall</text>

            {/* Stage boundary */}
            <line x1={startX+110} y1={outerY-25} x2={startX+110} y2={innerY+15}
              stroke="#c0392b" strokeWidth="1" strokeDasharray="4,3" opacity="0.5"/>
            <line x1={startX+345} y1={outerY-25} x2={startX+345} y2={innerY+15}
              stroke="#c0392b" strokeWidth="1" strokeDasharray="4,3" opacity="0.5"/>
            <line x1={startX+107} y1={outerY-22} x2={startX+348} y2={outerY-22}
              stroke="#c0392b" strokeWidth="1.5" markerEnd="url(#acArr)" markerStart="url(#acArr)"/>
            <text x={(startX+110+startX+345)/2} y={outerY-26} textAnchor="middle" fontSize="11" fill="#c0392b">One stage (rotor + stator)</text>
          </>;
        })()}

        {/* Right panel: h-s diagram for a stage */}
        <rect x={670} y={15} width={220} height={295} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={780} y={32} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Enthalpy–Entropy (h-s)</text>
        <text x={780} y={46} textAnchor="middle" fontSize="12" fill="#7B0D0D">Diagram for One Stage</text>

        {/* h-s axes */}
        <line x1={690} y1={280} x2={880} y2={280} stroke="#333" strokeWidth="1.5"/>
        <line x1={690} y1={280} x2={690} y2={55} stroke="#333" strokeWidth="1.5"/>
        <text x={884} y={284} fontSize="11" fill="#333">s</text>
        <text x={684} y={52} fontSize="11" fill="#333">h</text>

        {/* Isentropic compression (vertical line) */}
        <line x1={750} y1={240} x2={750} y2={100} stroke="#555" strokeWidth="1.5" strokeDasharray="5,3"/>

        {/* Actual compression (tilted right — entropy increase) */}
        <path d={`M 750,240 C 762,220 768,180 780,100`}
          fill="none" stroke="#c0392b" strokeWidth="2.5"/>

        {/* Points */}
        <circle cx={750} cy={240} r={4} fill="#2980b9"/>
        <text x={756} y={252} fontSize="10" fill="#2980b9" fontWeight="700">1 (inlet)</text>
        <circle cx={780} cy={100} r={4} fill="#c0392b"/>
        <text x={786} y={96} fontSize="10" fill="#c0392b" fontWeight="700">2 (outlet)</text>
        <circle cx={750} cy={100} r={4} fill="#27ae60"/>
        <text x={695} y={96} fontSize="10" fill="#27ae60">2s (ideal)</text>

        {/* Work arrows */}
        <line x1={835} y1={240} x2={835} y2={100} stroke="#e07b00" strokeWidth="2" markerEnd="url(#acArr)" markerStart="url(#acArr)"/>
        <text x={840} y={180} fontSize="11" fill="#e07b00" fontWeight="700">W_stage</text>
        <text x={840} y={194} fontSize="10" fill="#e07b00">= h₂ − h₁</text>

        {/* Isentropic work */}
        <line x1={700} y1={240} x2={700} y2={100} stroke="#27ae60" strokeWidth="2" markerEnd="url(#acArrG)" markerStart="url(#acArrG)"/>
        <text x={675} y={180} fontSize="10" fill="#27ae60" textAnchor="middle">W_is</text>

        {/* Efficiency label */}
        <text x={780} y={262} textAnchor="middle" fontSize="11" fill="#555" fontWeight="700">η_is = W_is / W_actual</text>
        <text x={780} y={276} textAnchor="middle" fontSize="10" fill="#555">= (h₂s−h₁)/(h₂−h₁)</text>
      </svg>
      <div style={S.dCaption}>An axial compressor stage consists of a rotor (which does work on the fluid, increasing total enthalpy) followed by a stator (which converts swirl kinetic energy to pressure isentropically). Each stage achieves a small pressure ratio (~1.2–1.4); multi-stage machines stack 10–20 stages to achieve overall ratios of 20–50:1 in modern jet engines. The h-s diagram shows the ideal isentropic compression (vertical line, Δs = 0) and the actual process (rightward, with entropy generation due to friction and flow separation). Isentropic efficiency η_is = (h₂s − h₁)/(h₂ − h₁) measures how close the stage is to ideal.</div>
    </div>
  );
}

// ── Diagram 5: Similarity Laws & Specific Speed ──────────────────
function DiagramSimilarityLaws() {
  const W = 900, H = 310;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Dimensional Similarity — Specific Speed and Machine Selection Chart</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="ssArr" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#333"/>
          </marker>
        </defs>

        {/* Ns axis (log-like) */}
        <line x1={50} y1={220} x2={860} y2={220} stroke="#333" strokeWidth="2" markerEnd="url(#ssArr)"/>
        <text x={870} y={224} fontSize="12" fill="#333" fontWeight="700">Ns  (specific speed)</text>
        <text x={50} y={240} fontSize="11" fill="#555">Low Ns (high H, low Q)</text>
        <text x={780} y={240} textAnchor="end" fontSize="11" fill="#555">High Ns (low H, high Q)</text>

        {/* Tick labels — approximate positions */}
        {[
          {x:100, label:'5'},
          {x:220, label:'20'},
          {x:360, label:'50'},
          {x:490, label:'100'},
          {x:620, label:'200'},
          {x:730, label:'400'},
          {x:830, label:'800'},
        ].map(({x,label})=>(
          <g key={label}>
            <line x1={x} y1={217} x2={x} y2={223} stroke="#333" strokeWidth="1.2"/>
            <text x={x} y={236} textAnchor="middle" fontSize="11" fill="#555">{label}</text>
          </g>
        ))}

        {/* Machine type bands */}
        {[
          { x1:55,  x2:195, label:'Radial (centrifugal)', sub:'Pumps & compressors', fill:'#FEF0F0', stroke:'#c0392b', tc:'#7B0D0D', shape:'radial' },
          { x1:200, x2:430, label:'Mixed flow', sub:'Pumps & fans', fill:'#fff5e6', stroke:'#e07b00', tc:'#8B4513', shape:'mixed' },
          { x1:435, x2:855, label:'Axial flow', sub:'Pumps, fans & compressors', fill:'#e8f4e8', stroke:'#27ae60', tc:'#145214', shape:'axial' },
        ].map(({x1,x2,label,sub,fill,stroke,tc,shape})=>(
          <g key={label}>
            <rect x={x1} y={60} width={x2-x1} height={148} rx={4} fill={fill} stroke={stroke} strokeWidth="1.5" opacity="0.7"/>
            <text x={(x1+x2)/2} y={78} textAnchor="middle" fontSize="13" fill={tc} fontWeight="700">{label}</text>
            <text x={(x1+x2)/2} y={93} textAnchor="middle" fontSize="11" fill={tc}>{sub}</text>
          </g>
        ))}

        {/* Turbine band (Pelton, Francis, Kaplan) — second row of bands */}
        {[
          { x1:55,  x2:200, label:"Pelton", sub:"(impulse, jets)", fill:"#f5e6ff", stroke:"#9b59b6", tc:"#5b2c8d" },
          { x1:210, x2:480, label:"Francis", sub:"(mixed-flow turbine)", fill:"#FEF0F0", stroke:"#c0392b", tc:"#7B0D0D" },
          { x1:490, x2:855, label:"Kaplan / Propeller", sub:"(axial-flow turbine)", fill:"#e8f4e8", stroke:"#27ae60", tc:"#145214" },
        ].map(({x1,x2,label,sub,fill,stroke,tc})=>(
          <g key={label}>
            <rect x={x1} y={145} width={x2-x1} height={62} rx={4} fill={fill} stroke={stroke} strokeWidth="1.5" opacity="0.7"/>
            <text x={(x1+x2)/2} y={165} textAnchor="middle" fontSize="12" fill={tc} fontWeight="700">{label}</text>
            <text x={(x1+x2)/2} y={180} textAnchor="middle" fontSize="10" fill={tc}>{sub}</text>
            <text x={(x1+x2)/2} y={194} textAnchor="middle" fontSize="10" fill={tc}>(turbine)</text>
          </g>
        ))}

        {/* Pump legend */}
        <rect x={52} y={62} width={148} height={60} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5" opacity="0.95"/>
        <text x={126} y={79} textAnchor="middle" fontSize="11" fill="#7B0D0D" fontWeight="700">Radial / Centrifugal</text>
        <text x={126} y={93} textAnchor="middle" fontSize="10" fill="#444">Ns ≈ 5–30 (pumps)</text>
        <text x={126} y={107} textAnchor="middle" fontSize="10" fill="#444">High head, low flow</text>

        {/* Impeller shapes drawn in each zone */}
        {/* Centrifugal impeller sketch */}
        {(()=>{
          const cx=126, cy=140;
          return <>
            <circle cx={cx} cy={cy} r={16} fill="#e8c4c4" stroke="#c0392b" strokeWidth="1.5"/>
            {[0,60,120,180,240,300].map(deg=>{
              const a=deg*Math.PI/180;
              return <line key={deg} x1={cx+7*Math.cos(a)} y1={cy+7*Math.sin(a)} x2={cx+15*Math.cos(a+0.4)} y2={cy+15*Math.sin(a+0.4)} stroke="#7B0D0D" strokeWidth="1.5"/>;
            })}
          </>;
        })()}

        {/* Mixed flow sketch */}
        {(()=>{
          const cx=315, cy=140;
          return <>
            <ellipse cx={cx} cy={cy} rx={22} ry={14} fill="#fff5e6" stroke="#e07b00" strokeWidth="1.5" transform={`rotate(-20,${cx},${cy})`}/>
            {[0,60,120].map(deg=>{
              const a=deg*Math.PI/180;
              return <line key={deg} x1={cx+9*Math.cos(a)} y1={cy+6*Math.sin(a)} x2={cx+20*Math.cos(a+0.3)} y2={cy+12*Math.sin(a+0.3)} stroke="#8B4513" strokeWidth="1.5"/>;
            })}
          </>;
        })()}

        {/* Axial fan sketch */}
        {(()=>{
          const cx=645, cy=140;
          return <>
            <circle cx={cx} cy={cy} r={16} fill="#e8f4e8" stroke="#27ae60" strokeWidth="1.5"/>
            {[0,72,144,216,288].map(deg=>{
              const a=deg*Math.PI/180;
              return <ellipse key={deg} cx={cx+11*Math.cos(a)} cy={cy+11*Math.sin(a)} rx={4} ry={8} fill="#27ae60" opacity="0.5" transform={`rotate(${deg+70},${cx+11*Math.cos(a)},${cy+11*Math.sin(a)})`}/>;
            })}
            <circle cx={cx} cy={cy} r={5} fill="#aaa"/>
          </>;
        })()}

        {/* Formula panel */}
        <rect x={52} y={250} width={800} height={50} rx={4} fill="#fff5f5" stroke="#f0cece"/>
        <text x={58} y={266} fontSize="12" fill="#7B0D0D" fontWeight="700">Specific speed (dimensionless):  Ns = N Q^(1/2) / (gH)^(3/4)</text>
        <text x={58} y={282} fontSize="11" fill="#444">Dimensional (pumps): Ns = N(rpm) · Q(m³/s)^(1/2) / H(m)^(3/4)   |   For turbines: Ns = N P^(1/2) / H^(5/4)</text>
        <text x={58} y={296} fontSize="11" fill="#444">Similarity laws: Q ∝ ND³,  H ∝ N²D²,  P ∝ ρN³D⁵   (for geometrically similar machines, constant ρ)</text>
      </svg>
      <div style={S.dCaption}>Specific speed Ns = NQ^(1/2)/(gH)^(3/4) is the key dimensionless parameter that characterises a turbomachine design and dictates optimal impeller geometry. Low specific speed (high head, low flow) favours radial (centrifugal) designs; high specific speed (low head, high flow) favours axial designs; intermediate values favour mixed-flow designs. The same principle applies to turbines: Pelton wheels at low Ns (high head), Francis turbines at medium Ns, Kaplan/propeller turbines at high Ns. The similarity laws allow performance at one speed/size to be scaled to any other geometrically similar machine.</div>
    </div>
  );
}

// ── Diagram 6: Gas Turbine (Brayton Cycle) ───────────────────────
function DiagramBraytonCycle() {
  const W = 900, H = 330;
  const PW = (W - 30) / 2;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Gas Turbine Engine — Brayton Cycle Layout and T-s Diagram</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="btArr" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#c0392b"/>
          </marker>
          <marker id="btArrG" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#27ae60"/>
          </marker>
          <marker id="btArrO" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#e07b00"/>
          </marker>
        </defs>

        {/* Left: Engine layout schematic */}
        <rect x={10} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={10+PW/2} y={27} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Gas Turbine — Component Layout</text>

        {(()=>{
          const y_top=55, y_bot=240, cx_mid=PW/2+10;
          // Flow path top/bottom lines — converging into combustor
          return <>
            {/* Compressor section */}
            <line x1={20} y1={y_top} x2={170} y2={y_top+15} stroke="#555" strokeWidth="2.5"/>
            <line x1={20} y1={y_bot} x2={170} y2={y_bot-15} stroke="#555" strokeWidth="2.5"/>
            <text x={95} y={y_top-8} textAnchor="middle" fontSize="13" fill="#c0392b" fontWeight="700">COMPRESSOR</text>

            {/* Compressor blades */}
            {[0,1,2,3].map(i=>{
              const bx=40+i*32;
              return <g key={i}>
                <line x1={bx} y1={y_top+2+i*3.5} x2={bx-8} y2={y_bot-2-i*3.5} stroke="#c0392b" strokeWidth="3"/>
              </g>;
            })}

            {/* Combustor */}
            <path d={`M 170,${y_top+15} C 180,${y_top+15} 185,${y_top+25} 185,${y_top+35} L 185,${y_bot-35} C 185,${y_bot-25} 180,${y_bot-15} 170,${y_bot-15} Z`}
              fill="#fff5e6" stroke="#e07b00" strokeWidth="2"/>
            <path d={`M 185,${y_top+35} L 270,${y_top+35} L 270,${y_bot-35} L 185,${y_bot-35} Z`}
              fill="#fff5e6" stroke="#e07b00" strokeWidth="2"/>
            <text x={227} y={(y_top+y_bot)/2-8} textAnchor="middle" fontSize="12" fill="#8B4513" fontWeight="700">COMBUSTOR</text>
            <text x={227} y={(y_top+y_bot)/2+6} textAnchor="middle" fontSize="11" fill="#e07b00">Q_in</text>
            {/* Flame symbol */}
            <path d={`M 225,${y_bot-70} C 215,${y_bot-90} 220,${y_bot-110} 228,${y_bot-100} C 218,${y_bot-120} 235,${y_bot-130} 228,${y_bot-100} C 238,${y_bot-120} 242,${y_bot-100} 232,${y_bot-85}`}
              fill="#e07b00" opacity="0.7"/>

            {/* Turbine section */}
            <line x1={270} y1={y_top+35} x2={420} y2={y_top+5} stroke="#555" strokeWidth="2.5"/>
            <line x1={270} y1={y_bot-35} x2={420} y2={y_bot-5} stroke="#555" strokeWidth="2.5"/>
            <text x={345} y={y_top-5} textAnchor="middle" fontSize="13" fill="#27ae60" fontWeight="700">TURBINE</text>

            {/* Turbine blades — expanding */}
            {[0,1,2,3].map(i=>{
              const bx=290+i*38;
              const spread=i*4;
              return <g key={i}>
                <line x1={bx} y1={y_top+35+2-spread} x2={bx+8} y2={y_bot-35-2+spread} stroke="#27ae60" strokeWidth="3"/>
              </g>;
            })}

            {/* Shaft connecting compressor and turbine */}
            <line x1={90} y1={(y_top+y_bot)/2} x2={380} y2={(y_top+y_bot)/2}
              stroke="#888" strokeWidth="5"/>
            <text x={235} y={(y_top+y_bot)/2+18} textAnchor="middle" fontSize="11" fill="#888">Shaft (W_c ≈ W_t × 60–70%)</text>

            {/* Inlet/outlet arrows */}
            <line x1={20} y1={(y_top+y_bot)/2} x2={55} y2={(y_top+y_bot)/2}
              stroke="#2980b9" strokeWidth="2.5" markerEnd="url(#btArr)"/>
            <text x={12} y={(y_top+y_bot)/2-10} fontSize="11" fill="#2980b9">Air in</text>
            <text x={12} y={(y_top+y_bot)/2+3} fontSize="10" fill="#2980b9">state 1</text>
            <line x1={400} y1={(y_top+y_bot)/2} x2={440} y2={(y_top+y_bot)/2}
              stroke="#27ae60" strokeWidth="2.5" markerEnd="url(#btArrG)"/>
            <text x={425} y={(y_top+y_bot)/2-10} fontSize="11" fill="#27ae60">Exhaust</text>
            <text x={425} y={(y_top+y_bot)/2+3} fontSize="10" fill="#27ae60">state 4</text>

            {/* State number labels */}
            {[
              {x:162,y:y_top+8,label:'2',color:'#c0392b'},
              {x:278,y:y_top+26,label:'3',color:'#e07b00'},
            ].map(({x,y,label,color})=>(
              <g key={label}>
                <circle cx={x} cy={y} r={9} fill={color} stroke="#fff" strokeWidth="1.5"/>
                <text x={x} y={y+4} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">{label}</text>
              </g>
            ))}

            {/* Net work annotation */}
            <rect x={18} y={255} width={PW-26} height={52} rx={3} fill="#e8f4e8" stroke="#27ae60" strokeWidth="1"/>
            <text x={24} y={271} fontSize="12" fill="#145214" fontWeight="700">W_net = W_turbine − W_compressor</text>
            <text x={24} y={287} fontSize="11" fill="#444">Back-work ratio = W_c/W_t ≈ 40–70% (gas turbines use most turbine work to drive compressor)</text>
            <text x={24} y={301} fontSize="11" fill="#444">Thermal efficiency: η_th = W_net / Q_in = 1 − T₁/T₂  (ideal Brayton, isentropic)</text>
          </>;
        })()}

        {/* Right: T-s diagram */}
        <rect x={PW+20} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={PW+20+PW/2} y={27} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">T–s Diagram (Brayton Cycle)</text>

        {(()=>{
          const ox=PW+50, oy=280, pw=PW-60, ch=240;
          const sx=(s)=>ox+(s/5)*pw;
          const ty=(T)=>oy-(T/1600)*ch;

          return <>
            {/* Axes */}
            <line x1={ox} y1={oy} x2={ox+pw+10} y2={oy} stroke="#333" strokeWidth="1.5"/>
            <line x1={ox} y1={oy} x2={ox} y2={oy-ch-10} stroke="#333" strokeWidth="1.5"/>
            <text x={ox+pw+14} y={oy+4} fontSize="12" fill="#333">s</text>
            <text x={ox-8} y={oy-ch-8} fontSize="12" fill="#333">T</text>

            {/* Process 1→2: isentropic compression (vertical) */}
            <line x1={sx(1)} y1={ty(300)} x2={sx(1)} y2={ty(700)}
              stroke="#c0392b" strokeWidth="2.8"/>
            {/* 1→2 actual (tilted) */}
            <path d={`M ${sx(1)},${ty(300)} C ${sx(1.3)},${ty(400)} ${sx(1.4)},${ty(580)} ${sx(1.6)},${ty(740)}`}
              fill="none" stroke="#c0392b" strokeWidth="2" strokeDasharray="5,3"/>

            {/* Process 2→3: constant pressure heat addition */}
            <path d={`M ${sx(1)},${ty(700)} C ${sx(2)},${ty(900)} ${sx(3)},${ty(1300)} ${sx(3.8)},${ty(1500)}`}
              fill="none" stroke="#e07b00" strokeWidth="2.8"/>

            {/* Process 3→4: isentropic expansion */}
            <path d={`M ${sx(3.8)},${ty(1500)} L ${sx(3.8)},${ty(650)}`}
              fill="none" stroke="#27ae60" strokeWidth="2.8"/>
            {/* 3→4 actual (tilted) */}
            <path d={`M ${sx(3.8)},${ty(1500)} C ${sx(4.1)},${ty(1300)} ${sx(4.2)},${ty(900)} ${sx(4.4)},${ty(700)}`}
              fill="none" stroke="#27ae60" strokeWidth="2" strokeDasharray="5,3"/>

            {/* Process 4→1: constant pressure heat rejection */}
            <path d={`M ${sx(3.8)},${ty(650)} C ${sx(3)},${ty(550)} ${sx(2)},${ty(400)} ${sx(1)},${ty(300)}`}
              fill="none" stroke="#2980b9" strokeWidth="2.8"/>

            {/* Fill cycle area */}
            <path d={`M ${sx(1)},${ty(300)} L ${sx(1)},${ty(700)} C ${sx(2)},${ty(900)} ${sx(3)},${ty(1300)} ${sx(3.8)},${ty(1500)} L ${sx(3.8)},${ty(650)} C ${sx(3)},${ty(550)} ${sx(2)},${ty(400)} ${sx(1)},${ty(300)} Z`}
              fill="#FEF0F0" opacity="0.4"/>

            {/* State point circles */}
            {[
              {s:1, T:300, label:'1', color:'#2980b9'},
              {s:1, T:700, label:'2', color:'#c0392b'},
              {s:3.8, T:1500, label:'3', color:'#e07b00'},
              {s:3.8, T:650, label:'4', color:'#27ae60'},
            ].map(({s,T,label,color})=>(
              <g key={label}>
                <circle cx={sx(s)} cy={ty(T)} r={6} fill={color} stroke="#fff" strokeWidth="1.5"/>
                <text x={sx(s)+8} y={ty(T)+4} fontSize="12" fill={color} fontWeight="700">{label}</text>
              </g>
            ))}

            {/* Labels */}
            <text x={ox+pw*0.05} y={ty(500)} fontSize="11" fill="#c0392b" fontWeight="700">1→2</text>
            <text x={ox+pw*0.05} y={ty(500)+14} fontSize="10" fill="#c0392b">Compression</text>
            <text x={sx(2.3)} y={ty(1300)} fontSize="11" fill="#e07b00" fontWeight="700">2→3  Heat in (Q_in)</text>
            <text x={sx(3.8)+8} y={ty(1100)} fontSize="11" fill="#27ae60" fontWeight="700">3→4</text>
            <text x={sx(3.8)+8} y={ty(1100)+14} fontSize="10" fill="#27ae60">Expansion</text>
            <text x={sx(2.4)} y={ty(380)} fontSize="11" fill="#2980b9" fontWeight="700">4→1  Heat out (Q_out)</text>

            {/* T_max label */}
            <text x={sx(3.8)-8} y={ty(1500)-8} textAnchor="end" fontSize="11" fill="#e07b00">T_TIT</text>
            <text x={sx(1)-8} y={ty(300)+4} textAnchor="end" fontSize="11" fill="#2980b9">T_amb</text>

            {/* Work net = enclosed area */}
            <text x={sx(2.4)} y={ty(750)} textAnchor="middle" fontSize="12" fill="#c0392b" fontWeight="700">W_net</text>
            <text x={sx(2.4)} y={ty(750)+14} textAnchor="middle" fontSize="11" fill="#555">(enclosed area)</text>

            {/* Dashed lines — actual cycle */}
            <line x1={ox+pw-30} y1={oy-18} x2={ox+pw-10} y2={oy-18} stroke="#555" strokeWidth="2" strokeDasharray="5,3"/>
            <text x={ox+pw+14} y={oy-14} fontSize="10" fill="#555">Actual</text>
          </>;
        })()}
      </svg>
      <div style={S.dCaption}>The Brayton cycle is the thermodynamic basis of all gas turbine engines (jet engines, power turbines). Air is isentropically compressed (1→2), heated at constant pressure in the combustor (2→3), then expanded through the turbine (3→4) before heat rejection (4→1). The turbine Inlet Temperature (TIT, state 3) is the most critical design parameter — higher TIT raises both power output and efficiency but demands advanced materials and cooling. The net work is the enclosed area on the T-s diagram. Real cycles include irreversibilities (dashed lines), reducing efficiency below the ideal η_th = 1 − T₁/T₂.</div>
    </div>
  );
}

// ── Diagram 7: Hydraulic Turbines — Pelton, Francis, Kaplan ──────
function DiagramHydraulicTurbines() {
  const W = 900, H = 320;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Hydraulic Turbines — Pelton (Impulse), Francis and Kaplan (Reaction)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="htArr" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto">
            <polygon points="0 0, 5 3, 0 6" fill="#c0392b"/>
          </marker>
          <marker id="htArrB" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto">
            <polygon points="0 0, 5 3, 0 6" fill="#2980b9"/>
          </marker>
          <marker id="htArrG" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto">
            <polygon points="0 0, 5 3, 0 6" fill="#27ae60"/>
          </marker>
        </defs>

        {/* ── Pelton wheel ── */}
        <rect x={10} y={10} width={255} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={137} y={27} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Pelton Wheel (Impulse)</text>

        {(()=>{
          const cx=137, cy=155, R=80;
          // Buckets around rim
          return <>
            {/* Wheel */}
            <circle cx={cx} cy={cy} r={R} fill="#f5e6ff" stroke="#9b59b6" strokeWidth="2"/>
            <circle cx={cx} cy={cy} r={28} fill="#ddd" stroke="#555" strokeWidth="1.5"/>
            {/* Buckets — 8, double-cup shape */}
            {Array.from({length:10},(_,i)=>{
              const a=i*36*Math.PI/180;
              const bx=cx+(R-12)*Math.cos(a), by=cy+(R-12)*Math.sin(a);
              return <g key={i}>
                <ellipse cx={bx} cy={by} rx={14} ry={8} fill="#9b59b6" opacity="0.6"
                  stroke="#5b2c8d" strokeWidth="1"
                  transform={`rotate(${i*36+90},${bx},${by})`}/>
                {/* Splitter ridge */}
                <line x1={bx-5*Math.sin(a+(90+i*36)*Math.PI/180)} y1={by+5*Math.cos(a+(90+i*36)*Math.PI/180)}
                      x2={bx+5*Math.sin(a+(90+i*36)*Math.PI/180)} y2={by-5*Math.cos(a+(90+i*36)*Math.PI/180)}
                  stroke="#fff" strokeWidth="1.5"/>
              </g>;
            })}
            {/* Nozzle jet */}
            <line x1={cx-R-60} y1={cy} x2={cx-R-2} y2={cy}
              stroke="#2980b9" strokeWidth="5" markerEnd="url(#htArrB)"/>
            <text x={cx-R-50} y={cy-12} fontSize="12" fill="#2980b9" fontWeight="700">Jet (V_j)</text>
            <text x={cx-R-50} y={cy-26} fontSize="11" fill="#2980b9">high vel., atm. p</text>
            {/* Nozzle tip */}
            <polygon points={`${cx-R-12},${cy-8} ${cx-R},${cy} ${cx-R-12},${cy+8}`} fill="#555"/>
            {/* Rotation */}
            <path d={`M ${cx+R-10},${cy-15} A ${R-10} ${R-10} 0 0 0 ${cx+15},${cy+R-10}`}
              fill="none" stroke="#e07b00" strokeWidth="2" markerEnd="url(#htArr)"/>
            <text x={cx+70} y={cy+50} fontSize="12" fill="#e07b00" fontWeight="700">ω</text>
            <text x={cx} y={H-40} textAnchor="middle" fontSize="11" fill="#7B0D0D" fontWeight="700">Ns ≈ 5–35</text>
            <text x={cx} y={H-26} textAnchor="middle" fontSize="10" fill="#444">High head (100–2000 m)</text>
            <text x={cx} y={H-12} textAnchor="middle" fontSize="10" fill="#444">η_max ≈ 90–93%</text>
            <text x={cx} y={H-56} textAnchor="middle" fontSize="10" fill="#9b59b6">All pressure drop in nozzle</text>
            <text x={cx} y={H-70} textAnchor="middle" fontSize="10" fill="#9b59b6">(p_bucket = p_atm throughout)</text>
          </>;
        })()}

        {/* ── Francis turbine ── */}
        <rect x={278} y={10} width={300} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={428} y={27} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Francis Turbine (Reaction, mixed-flow)</text>

        {(()=>{
          const cx=428, cy=148;
          return <>
            {/* Spiral casing (volute) */}
            <path d={`M ${cx-105},${cy} A 105 105 0 1 1 ${cx+105},${cy} L ${cx+60},${cy+80} A 70 70 0 1 0 ${cx-70},${cy+20} Z`}
              fill="#fff5e6" stroke="#e07b00" strokeWidth="2"/>
            {/* Runner */}
            <circle cx={cx} cy={cy} r={65} fill="#FEF0F0" stroke="#c0392b" strokeWidth="2"/>
            {/* Runner blades — 8, backward curved */}
            {Array.from({length:8},(_,i)=>{
              const a=i*45*Math.PI/180;
              const r1=28, r2=62;
              return <path key={i}
                d={`M ${cx+r1*Math.cos(a)},${cy+r1*Math.sin(a)} C ${cx+42*Math.cos(a+0.4)},${cy+42*Math.sin(a+0.4)} ${cx+56*Math.cos(a+0.7)},${cy+56*Math.sin(a+0.7)} ${cx+r2*Math.cos(a+0.9)},${cy+r2*Math.sin(a+0.9)}`}
                fill="none" stroke="#c0392b" strokeWidth="2.5"/>;
            })}
            {/* Hub / shaft */}
            <circle cx={cx} cy={cy} r={20} fill="#aaa" stroke="#555" strokeWidth="1.5"/>
            {/* Guide vanes (stay vanes) */}
            {Array.from({length:10},(_,i)=>{
              const a=i*36*Math.PI/180;
              const r=82;
              return <line key={i}
                x1={cx+(r-8)*Math.cos(a)} y1={cy+(r-8)*Math.sin(a)}
                x2={cx+(r+8)*Math.cos(a+0.3)} y2={cy+(r+8)*Math.sin(a+0.3)}
                stroke="#e07b00" strokeWidth="2.5"/>;
            })}
            {/* Inlet arrow */}
            <line x1={cx-105} y1={cy-30} x2={cx-80} y2={cy-20}
              stroke="#2980b9" strokeWidth="2.5" markerEnd="url(#htArrB)"/>
            <text x={cx-120} y={cy-38} fontSize="11" fill="#2980b9" fontWeight="700">Water in</text>
            <text x={cx-120} y={cy-25} fontSize="10" fill="#2980b9">(high p, radial)</text>
            {/* Draft tube / outlet arrow */}
            <rect x={cx-18} y={cy+65} width={36} height={50} rx={4} fill="#e8f4e8" stroke="#27ae60" strokeWidth="2"/>
            <line x1={cx} y1={cy+115} x2={cx} y2={cy+145}
              stroke="#27ae60" strokeWidth="2.5" markerEnd="url(#htArrG)"/>
            <text x={cx+14} y={cy+138} fontSize="11" fill="#27ae60">Draft tube</text>
            <text x={cx+14} y={cy+150} fontSize="10" fill="#27ae60">(axial exit)</text>
            {/* Rotation */}
            <path d={`M ${cx+50},${cy-35} A 50 50 0 0 1 ${cx+35},${cy+50}`}
              fill="none" stroke="#e07b00" strokeWidth="2" markerEnd="url(#htArr)"/>

            <text x={cx} y={H-40} textAnchor="middle" fontSize="11" fill="#7B0D0D" fontWeight="700">Ns ≈ 50–250</text>
            <text x={cx} y={H-26} textAnchor="middle" fontSize="10" fill="#444">Medium head (20–700 m)</text>
            <text x={cx} y={H-12} textAnchor="middle" fontSize="10" fill="#444">η_max ≈ 90–95%</text>
          </>;
        })()}

        {/* ── Kaplan turbine ── */}
        <rect x={591} y={10} width={299} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={740} y={27} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Kaplan Turbine (Reaction, axial)</text>

        {(()=>{
          const cx=740, cy=148, R=75;
          return <>
            {/* Outer casing */}
            <circle cx={cx} cy={cy} r={R+15} fill="#e8f4e8" stroke="#27ae60" strokeWidth="2"/>
            {/* Hub */}
            <circle cx={cx} cy={cy} r={28} fill="#aaa" stroke="#555" strokeWidth="1.5"/>
            {/* Propeller blades — 4 wide, adjustable pitch */}
            {[0,90,180,270].map(deg=>{
              const a=(deg+20)*Math.PI/180;
              const bx=cx+28*Math.cos(a), by=cy+28*Math.sin(a);
              const ex=cx+R*Math.cos(a-0.1), ey=cy+R*Math.sin(a-0.1);
              return <g key={deg}>
                <path d={`M ${bx},${by} C ${cx+48*Math.cos(a-0.05)},${cy+48*Math.sin(a-0.05)} ${cx+62*Math.cos(a-0.08)},${cy+62*Math.sin(a-0.08)} ${ex},${ey}`}
                  fill="#27ae60" stroke="#145214" strokeWidth="1.5" opacity="0.5"/>
                <path d={`M ${cx+28*Math.cos(a+0.18)},${cy+28*Math.sin(a+0.18)} C ${cx+48*Math.cos(a+0.13)},${cy+48*Math.sin(a+0.13)} ${cx+62*Math.cos(a+0.09)},${cy+62*Math.sin(a+0.09)} ${cx+R*Math.cos(a+0.1)},${cy+R*Math.sin(a+0.1)}`}
                  fill="#27ae60" stroke="#145214" strokeWidth="1.5" opacity="0.5"/>
              </g>;
            })}
            {/* Inlet arrows (axial — from left) */}
            {[cy-50,cy-20,cy+20,cy+50].map(y=>(
              <line key={y} x1={cx-R-42} y1={y} x2={cx-R-8} y2={y}
                stroke="#2980b9" strokeWidth="2" markerEnd="url(#htArrB)"/>
            ))}
            <text x={cx-R-50} y={cy-60} textAnchor="end" fontSize="11" fill="#2980b9" fontWeight="700">Water in</text>
            <text x={cx-R-50} y={cy-47} textAnchor="end" fontSize="10" fill="#2980b9">(axial)</text>
            {/* Exit arrows */}
            {[cy-50,cy-20,cy+20,cy+50].map(y=>(
              <line key={y} x1={cx+R+8} y1={y} x2={cx+R+40} y2={y}
                stroke="#27ae60" strokeWidth="2" markerEnd="url(#htArrG)"/>
            ))}
            <text x={cx+R+44} y={cy-60} fontSize="11" fill="#27ae60" fontWeight="700">Water out</text>

            {/* Guide vane arrow */}
            <text x={cx} y={cy-R-6} textAnchor="middle" fontSize="10" fill="#e07b00" fontWeight="700">Adjustable runner blades</text>
            {/* Rotation */}
            <path d={`M ${cx-20},${cy-R+8} A ${R-8} ${R-8} 0 0 0 ${cx+20},${cy-R+8}`}
              fill="none" stroke="#e07b00" strokeWidth="2" markerEnd="url(#htArr)"/>

            <text x={cx} y={H-40} textAnchor="middle" fontSize="11" fill="#7B0D0D" fontWeight="700">Ns ≈ 250–800</text>
            <text x={cx} y={H-26} textAnchor="middle" fontSize="10" fill="#444">Low head (2–40 m)</text>
            <text x={cx} y={H-12} textAnchor="middle" fontSize="10" fill="#444">η_max ≈ 88–93%</text>
            <text x={cx} y={H-56} textAnchor="middle" fontSize="10" fill="#27ae60">Double-regulated (guide + runner)</text>
            <text x={cx} y={H-70} textAnchor="middle" fontSize="10" fill="#27ae60">maintains η over wide Q range</text>
          </>;
        })()}
      </svg>
      <div style={S.dCaption}>The three main hydraulic turbine types span the full range of specific speed. The Pelton wheel (impulse) uses one or more jets to drive double-cup buckets at atmospheric pressure — all pressure drop occurs in the nozzle. Francis turbines (reaction, mixed-flow) take high-pressure water through spiral casing and guide vanes into the runner, with pressure falling across both guide vanes and runner. Kaplan turbines (reaction, axial-flow) are propeller turbines with adjustable blade pitch, allowing efficient operation over a wide flow range — essential for run-of-river hydro plants with variable flow. All reaction turbines benefit from a draft tube which recovers the exit kinetic energy by creating a partial vacuum at the runner exit.</div>
    </div>
  );
}

// ── Diagram 8: Pump Affinity Laws & Series/Parallel ───────────────
function DiagramAffinityLaws() {
  const W = 900, H = 310;
  const PW = (W - 30) / 2;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Affinity Laws — Speed Scaling, Series and Parallel Pump Operation</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="alArr" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#333"/>
          </marker>
          <marker id="alArrR" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#c0392b"/>
          </marker>
          <marker id="alArrG" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#27ae60"/>
          </marker>
          <marker id="alArrB" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#9b59b6"/>
          </marker>
        </defs>

        {/* Left: Affinity law H-Q curves at different speeds */}
        <rect x={10} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={10+PW/2} y={27} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Affinity Laws — H-Q Curves at Different Speeds</text>

        {(()=>{
          const ox=35, oy=35, pw=PW-50, ch=220;
          const qx=(Q)=>ox+(Q/400)*pw;
          const hy=(H)=>oy+ch-(H/280)*ch;
          const speeds=[{N:1.0,c:'#c0392b',lbl:'N (design)'},{N:0.8,c:'#e07b00',lbl:'0.8N'},{N:0.6,c:'#27ae60',lbl:'0.6N'}];
          const H0=260, kH=0.0016;

          return <>
            {/* Axes */}
            <line x1={ox} y1={oy+ch} x2={ox+pw+10} y2={oy+ch} stroke="#333" strokeWidth="1.5" markerEnd="url(#alArr)"/>
            <line x1={ox} y1={oy} x2={ox} y2={oy+ch+10} stroke="#333" strokeWidth="1.5" markerEnd="url(#alArr)"/>
            <text x={ox+pw+14} y={oy+ch+4} fontSize="12" fill="#333">Q</text>
            <text x={ox-8} y={oy-6} fontSize="12" fill="#333">H</text>

            {speeds.map(({N,c,lbl})=>{
              const pts=[];
              for(let Q=0;Q<=400*N;Q+=5){
                const H=H0*N*N - kH*(Q/N)*(Q/N)*N*N;
                if(H>0) pts.push({Q,H});
              }
              const path=pts.map(({Q,H},i)=>`${i===0?'M':'L'} ${qx(Q)},${hy(H)}`).join(' ');
              // Shutoff and max flow labels
              return <>
                <path key={lbl} d={path} fill="none" stroke={c} strokeWidth="2.5"/>
                <text x={qx(5)} y={hy(H0*N*N)+4} fontSize="11" fill={c} fontWeight="700">{lbl}</text>
              </>;
            })}

            {/* Affinity parabola (locus of similar operating points) */}
            {(()=>{
              const pts=[];
              for(let Q=0;Q<=380;Q+=4){
                const H=kH*1.02*Q*Q;
                if(H<280) pts.push({Q,H});
              }
              const path=pts.map(({Q,H},i)=>`${i===0?'M':'L'} ${qx(Q)},${hy(H)}`).join(' ');
              return <path d={path} fill="none" stroke="#9b59b6" strokeWidth="1.8" strokeDasharray="6,3"/>;
            })()}
            <text x={qx(280)} y={hy(kH*1.02*280*280)-8} fontSize="11" fill="#9b59b6">Affinity parabola</text>
            <text x={qx(280)} y={hy(kH*1.02*280*280)+6} fontSize="11" fill="#9b59b6">(similar points)</text>

            {/* BEP locus markers */}
            {[1.0,0.8,0.6].map((N,i)=>{
              const Qbep=220*N, Hbep=H0*N*N-kH*Qbep*Qbep/(N*N)*N*N;
              return <circle key={N} cx={qx(Qbep)} cy={hy(Hbep)} r={5}
                fill={['#c0392b','#e07b00','#27ae60'][i]} stroke="#fff" strokeWidth="1.5"/>;
            })}

            {/* Affinity law text */}
            <rect x={ox+10} y={oy+ch-110} width={pw-20} height={100} rx={3} fill="#fff5f5" stroke="#f0cece"/>
            <text x={ox+16} y={oy+ch-94} fontSize="12" fill="#7B0D0D" fontWeight="700">Affinity (similarity) laws  (fixed geometry, vary N):</text>
            <text x={ox+16} y={oy+ch-76} fontSize="12" fill="#c0392b" fontFamily="'Courier New',monospace">  Q₂/Q₁ = N₂/N₁</text>
            <text x={ox+16} y={oy+ch-58} fontSize="12" fill="#e07b00" fontFamily="'Courier New',monospace">  H₂/H₁ = (N₂/N₁)²</text>
            <text x={ox+16} y={oy+ch-40} fontSize="12" fill="#27ae60" fontFamily="'Courier New',monospace">  P₂/P₁ = (N₂/N₁)³</text>
            <text x={ox+16} y={oy+ch-20} fontSize="11" fill="#555">Halving speed → 1/8 power — huge energy saving for variable-flow systems</text>
          </>;
        })()}

        {/* Right: Series and Parallel */}
        <rect x={PW+20} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={PW+20+PW/2} y={27} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Series and Parallel Pump Combinations</text>

        {(()=>{
          const ox=PW+45, oy=35, pw=PW-55, ch=245;
          const qx=(Q)=>ox+(Q/500)*pw;
          const hy=(H)=>oy+ch-(H/350)*ch;
          const H0=200, kH=0.0012;

          // Single pump curve
          const singlePts=[], seriesPts=[], parallelPts=[];
          for(let Q=0;Q<=420;Q+=5){
            const H=H0-kH*Q*Q;
            if(H>0) singlePts.push({Q,H});
          }
          for(let Q=0;Q<=420;Q+=5){
            const H=2*(H0-kH*Q*Q);
            if(H>0 && H<350) seriesPts.push({Q,H});
          }
          for(let Q=0;Q<=800;Q+=5){
            const H=H0-kH*(Q/2)*(Q/2);
            if(H>0 && Q<=pw/((1/500))+ox) parallelPts.push({Q,H});
          }

          const mkPath=(pts)=>pts.map(({Q,H},i)=>`${i===0?'M':'L'} ${qx(Q)},${hy(H)}`).join(' ');

          // System curve
          const sysPts=[];
          for(let Q=0;Q<=500;Q+=5){
            const H=30+0.0008*Q*Q;
            if(H<=350) sysPts.push({Q,H});
          }

          return <>
            {/* Axes */}
            <line x1={ox} y1={oy+ch} x2={ox+pw+10} y2={oy+ch} stroke="#333" strokeWidth="1.5" markerEnd="url(#alArr)"/>
            <line x1={ox} y1={oy} x2={ox} y2={oy+ch+10} stroke="#333" strokeWidth="1.5" markerEnd="url(#alArr)"/>
            <text x={ox+pw+14} y={oy+ch+4} fontSize="12" fill="#333">Q</text>
            <text x={ox-8} y={oy-6} fontSize="12" fill="#333">H</text>

            <path d={mkPath(singlePts)}   fill="none" stroke="#c0392b" strokeWidth="2.5"/>
            <path d={mkPath(seriesPts)}   fill="none" stroke="#e07b00" strokeWidth="2.5"/>
            <path d={mkPath(parallelPts)} fill="none" stroke="#27ae60" strokeWidth="2.5"/>
            <path d={mkPath(sysPts)}      fill="none" stroke="#9b59b6" strokeWidth="2" strokeDasharray="6,3"/>

            {/* Operating point labels */}
            <text x={qx(15)} y={hy(H0)+4} fontSize="11" fill="#c0392b" fontWeight="700">Single pump</text>
            <text x={qx(15)} y={hy(2*H0)+4} fontSize="11" fill="#e07b00" fontWeight="700">2 pumps in SERIES</text>
            <text x={qx(15)} y={hy(2*H0)+18} fontSize="10" fill="#e07b00">(same Q, double H at each Q)</text>
            <text x={qx(350)} y={hy(H0-kH*350*350)+14} fontSize="11" fill="#27ae60" fontWeight="700">2 pumps in PARALLEL</text>
            <text x={qx(350)} y={hy(H0-kH*350*350)+28} fontSize="10" fill="#27ae60">(same H, double Q at each H)</text>
            <text x={qx(350)} y={hy(30+0.0008*350*350)-10} fontSize="11" fill="#9b59b6">System curve</text>

            {/* Intersection dots */}
            {/* Single pump ∩ system */}
            {(()=>{
              // approx intersection: H0-kH*Q^2 = 30+0.0008*Q^2 → (0.0012+0.0008)*Q^2=170 → Q=√(170/0.002)=292
              const Q=292, H=H0-kH*Q*Q;
              return <circle cx={qx(Q)} cy={hy(H)} r={6} fill="#c0392b" stroke="#fff" strokeWidth="1.5"/>;
            })()}
            {/* Series ∩ system */}
            {(()=>{
              // 2*(H0-kH*Q^2)=30+0.0008*Q^2 → 2*H0-30=(2*kH+0.0008)*Q^2 → Q=√(370/0.0032)≈340
              const Q=340, H=2*(H0-kH*Q*Q);
              return <circle cx={qx(Q)} cy={hy(H)} r={6} fill="#e07b00" stroke="#fff" strokeWidth="1.5"/>;
            })()}
            {/* Parallel ∩ system */}
            {(()=>{
              // H0-kH*(Q/2)^2 = 30+0.0008*Q^2 → 200-0.0003*Q^2=30+0.0008*Q^2 → Q=√(170/0.0011)≈393
              const Q=393, H=H0-kH*(Q/2)*(Q/2);
              return <circle cx={qx(Q)} cy={hy(H)} r={6} fill="#27ae60" stroke="#fff" strokeWidth="1.5"/>;
            })()}
          </>;
        })()}
      </svg>
      <div style={S.dCaption}>The pump affinity laws state that for a geometrically similar pump: Q ∝ N, H ∝ N², P ∝ N³. The cubic power law for shaft power means that halving the rotational speed (via a VFD — variable frequency drive) reduces power consumption to just 1/8 — huge energy savings for variable-flow systems such as building HVAC and water distribution. For multi-pump systems: series connection doubles the head at each flow rate (useful for high-static-head systems); parallel connection doubles the flow rate at each head (useful for high-flow-rate systems with modest heads). The operating point is always at the intersection of the combined pump curve and system curve.</div>
    </div>
  );
}

// ── Diagram 9: Cavitation in Pumps ───────────────────────────────
function DiagramCavitation() {
  const W = 900, H = 290;
  const PW = (W - 30) / 2;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Cavitation — Bubble Formation, Collapse, and NPSH</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="cavArr" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto">
            <polygon points="0 0, 5 3, 0 6" fill="#c0392b"/>
          </marker>
          <marker id="cavArrG" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto">
            <polygon points="0 0, 5 3, 0 6" fill="#27ae60"/>
          </marker>
        </defs>

        {/* Left: Cavitation physics */}
        <rect x={10} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={10+PW/2} y={27} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Cavitation — Bubble Dynamics</text>

        {(()=>{
          const impR=80, cx=160, cy=148;
          return <>
            {/* Impeller sketch */}
            <circle cx={cx} cy={cy} r={impR} fill="#fff5f5" stroke="#c0392b" strokeWidth="1.5"/>
            {Array.from({length:6},(_,i)=>{
              const a=i*60*Math.PI/180;
              const r1=28, r2=76;
              return <path key={i}
                d={`M ${cx+r1*Math.cos(a)},${cy+r1*Math.sin(a)} C ${cx+50*Math.cos(a+0.3)},${cy+50*Math.sin(a+0.3)} ${cx+65*Math.cos(a+0.6)},${cy+65*Math.sin(a+0.6)} ${cx+r2*Math.cos(a+0.8)},${cy+r2*Math.sin(a+0.8)}`}
                fill="none" stroke="#c0392b" strokeWidth="2"
              />;
            })}
            <circle cx={cx} cy={cy} r={20} fill="#aaa" stroke="#555" strokeWidth="1"/>

            {/* Low-pressure zone annotation */}
            <path d={`M ${cx+45*Math.cos(0.4)},${cy-45*Math.sin(0.4)} A 20 12 0 1 1 ${cx+48*Math.cos(0.8)},${cy-45*Math.sin(0.8)}`}
              fill="#9b59b6" opacity="0.3"/>
            {/* Cavitation bubbles near leading edge */}
            {[[cx+55,cy-55],[cx+62,cy-38],[cx+68,cy-22],[cx+72,cy-5]].map(([bx,by],i)=>(
              <circle key={i} cx={bx} cy={by} r={4+i*1.5}
                fill="none" stroke="#9b59b6" strokeWidth="1.5" opacity="0.7"/>
            ))}
            <text x={cx+82} y={cy-58} fontSize="11" fill="#9b59b6" fontWeight="700">Bubbles form</text>
            <text x={cx+82} y={cy-44} fontSize="11" fill="#9b59b6">(p &lt; p_vapour)</text>
            <text x={cx+82} y={cy-30} fontSize="11" fill="#9b59b6">near blade</text>
            <text x={cx+82} y={cy-16} fontSize="11" fill="#9b59b6">leading edge</text>

            {/* Collapse zone */}
            {[[cx+50,cy+40],[cx+42,cy+52],[cx+38,cy+65]].map(([bx,by],i)=>(
              <g key={i}>
                <circle cx={bx} cy={by} r={6-i*1.5} fill="#c0392b" opacity="0.5"/>
                {/* Implosion lines */}
                {[0,45,90,135].map(a=>(
                  <line key={a}
                    x1={bx+(8-i)*Math.cos(a*Math.PI/180)} y1={by+(8-i)*Math.sin(a*Math.PI/180)}
                    x2={bx} y2={by}
                    stroke="#c0392b" strokeWidth="1" opacity="0.5"/>
                ))}
              </g>
            ))}
            <text x={cx-72} y={cy+52} textAnchor="end" fontSize="11" fill="#c0392b" fontWeight="700">Bubbles collapse</text>
            <text x={cx-72} y={cy+66} textAnchor="end" fontSize="11" fill="#c0392b">(micro-jets 500 m/s)</text>
            <text x={cx-72} y={cy+80} textAnchor="end" fontSize="11" fill="#c0392b">→ pitting, erosion,</text>
            <text x={cx-72} y={cy+94} textAnchor="end" fontSize="11" fill="#c0392b">noise, vibration</text>

            {/* Pressure profile along blade */}
            <rect x={14} y={H-68} width={PW-22} height={48} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
            <text x={20} y={H-52} fontSize="11" fill="#7B0D0D" fontWeight="700">Cavitation occurs when:  p_local ≤ p_vapour(T)</text>
            <text x={20} y={H-36} fontSize="11" fill="#444">p_vapour(water, 20°C) ≈ 2.34 kPa  |  At 80°C: ≈ 47.4 kPa</text>
            <text x={20} y={H-20} fontSize="11" fill="#444">Triggered by: high speed, low suction pressure, high temperature</text>
          </>;
        })()}

        {/* Right: NPSH diagram */}
        <rect x={PW+20} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={PW+20+PW/2} y={27} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">NPSH — Net Positive Suction Head</text>

        {(()=>{
          const ox=PW+45, oy=40, pw=PW-60, ch=160;
          const qx=(Q)=>ox+(Q/350)*pw;
          const hy=(H)=>oy+ch-(H/18)*ch;

          // NPSHr curve (required) — rises with Q
          const npshRPts=[];
          for(let Q=0;Q<=340;Q+=5){
            const n=1.5+0.00012*Q*Q;
            npshRPts.push({Q,n});
          }
          // NPSHa (available) — flat/slightly falling with Q
          const npshAPts=[];
          for(let Q=0;Q<=340;Q+=5){
            const n=12-0.00003*Q*Q;
            if(n>0) npshAPts.push({Q,n});
          }
          const mkPath=(pts,key='n')=>pts.map((p,i)=>`${i===0?'M':'L'} ${qx(p.Q)},${hy(p[key])}`).join(' ');

          // Critical Q where NPSHr = NPSHa: 1.5+0.00012*Q^2 = 12-0.00003*Q^2 → 0.00015*Q^2=10.5 → Q=265
          const Qcrit=265, Ncrit=1.5+0.00012*265*265;

          return <>
            {/* Axes */}
            <line x1={ox} y1={oy+ch} x2={ox+pw+10} y2={oy+ch} stroke="#333" strokeWidth="1.5" markerEnd="url(#cavArr)"/>
            <line x1={ox} y1={oy} x2={ox} y2={oy+ch+10} stroke="#333" strokeWidth="1.5" markerEnd="url(#cavArr)"/>
            <text x={ox+pw+14} y={oy+ch+4} fontSize="12" fill="#333">Q</text>
            <text x={ox-8} y={oy-6} fontSize="12" fill="#333">NPSH (m)</text>

            {/* Ticks */}
            {[0,5,10,15].map(v=>(
              <g key={v}>
                <line x1={ox-3} y1={hy(v)} x2={ox+3} y2={hy(v)} stroke="#333" strokeWidth="1"/>
                <text x={ox-6} y={hy(v)+4} textAnchor="end" fontSize="10" fill="#555">{v}</text>
              </g>
            ))}

            <path d={mkPath(npshRPts)} fill="none" stroke="#c0392b" strokeWidth="2.5"/>
            <path d={mkPath(npshAPts)} fill="none" stroke="#27ae60" strokeWidth="2.5"/>

            {/* Safe zone fill */}
            <path d={`${mkPath(npshAPts)} L ${qx(0)},${hy(12)} Z`} fill="#e8f4e8" opacity="0.4"/>

            {/* Labels */}
            <text x={qx(20)} y={hy(11.5)} fontSize="12" fill="#27ae60" fontWeight="700">NPSH_A (available)</text>
            <text x={qx(20)} y={hy(11.5)+14} fontSize="11" fill="#27ae60">= (p_s/ρg + V_s²/2g) − p_v/ρg</text>
            <text x={qx(200)} y={hy(9)} fontSize="12" fill="#c0392b" fontWeight="700">NPSH_R (required)</text>
            <text x={qx(200)} y={hy(9)+14} fontSize="11" fill="#c0392b">(pump manufacturer data)</text>

            {/* Cavitation zone */}
            <circle cx={qx(Qcrit)} cy={hy(Ncrit)} r={7} fill="#c0392b" stroke="#fff" strokeWidth="1.5"/>
            <line x1={qx(Qcrit)} y1={hy(Ncrit)} x2={qx(Qcrit)} y2={oy+ch}
              stroke="#c0392b" strokeWidth="1.5" strokeDasharray="4,3"/>
            <text x={qx(Qcrit)+6} y={hy(Ncrit)-8} fontSize="11" fill="#c0392b" fontWeight="700">Onset of cavitation</text>
            <text x={qx(Qcrit)+6} y={hy(Ncrit)+6} fontSize="10" fill="#c0392b">NPSH_A = NPSH_R</text>

            {/* Safe margin indicator */}
            <line x1={qx(180)} y1={hy(1.5+0.00012*180*180)} x2={qx(180)} y2={hy(12-0.00003*180*180)}
              stroke="#e07b00" strokeWidth="2" markerEnd="url(#cavArr)" markerStart="url(#cavArr)"/>
            <text x={qx(180)+6} y={hy(8)} fontSize="11" fill="#e07b00" fontWeight="700">Safety margin</text>
            <text x={qx(180)+6} y={hy(8)+14} fontSize="10" fill="#e07b00">NPSH_A ≥ NPSH_R + 0.5 m</text>

            {/* Safe/unsafe regions */}
            <text x={qx(100)} y={oy+ch-30} textAnchor="middle" fontSize="13" fill="#27ae60" fontWeight="700">SAFE</text>
            <text x={qx(300)} y={oy+ch-20} textAnchor="middle" fontSize="13" fill="#c0392b" fontWeight="700">CAVITATION</text>

            {/* NPSH formula boxes */}
            <rect x={ox} y={oy+ch+14} width={pw} height={52} rx={3} fill="#fff5f5" stroke="#f0cece"/>
            <text x={ox+6} y={oy+ch+28} fontSize="11" fill="#7B0D0D" fontWeight="700">NPSH_A = p_atm/(ρg) + z_s − p_vapour/(ρg) − h_f,suction</text>
            <text x={ox+6} y={oy+ch+44} fontSize="11" fill="#444">z_s = static suction lift (negative for suction below pump)</text>
            <text x={ox+6} y={oy+ch+58} fontSize="11" fill="#444">Thoma cavitation number: σ = NPSH_A / H_pump  (dimensionless)</text>
          </>;
        })()}
      </svg>
      <div style={S.dCaption}>Cavitation occurs when the local static pressure in a flow drops below the vapour pressure of the liquid — vapour bubbles nucleate and grow. When these bubbles are carried into a higher-pressure region they collapse violently, generating micro-jets up to 500 m/s and pressure spikes up to 1 GPa locally. This causes severe pitting erosion of metal surfaces, noise, vibration, and reduction in pump performance. The Net Positive Suction Head available (NPSH_A) must always exceed the manufacturer's required NPSH_R (plus safety margin). Cavitation is avoided by minimising suction lift, increasing suction pipe diameter, and selecting impeller speed and diameter carefully.</div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────
export default function Turbomachinery() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Turbomachinery in Fluid Dynamics</h1>
      <p style={S.p}>
        Turbomachinery encompasses all devices that exchange energy between a rotating mechanical element and a continuously flowing fluid. Pumps lift water, fans move air, compressors raise gas pressure, and turbines extract work from steam, gas, or water — all through the same fundamental mechanism: the rotor imparts or removes angular momentum from the fluid. Turbomachines are ubiquitous in modern civilisation: they pressurize every drop of municipal water, drive every commercial aircraft, generate the majority of the world's electricity, and compress the working fluid in refrigerators and air conditioners. Understanding the fluid mechanics of rotating flows is therefore one of the most practically consequential areas of engineering physics.
      </p>

      <InfoBox title="Core Ideas at a Glance" items={[
        "Euler turbomachine equation: W = U₂Cu₂ − U₁Cu₁  (specific work = change in angular momentum flux)",
        "Velocity triangle: C (absolute) = U (blade) + W (relative);  three vectors at inlet and outlet",
        "Specific speed Ns = NQ^(1/2)/(gH)^(3/4) selects machine type: low Ns → radial, high Ns → axial",
        "Affinity laws: Q ∝ N, H ∝ N², P ∝ N³  (for geometrically similar machines at different speeds)",
        "Best Efficiency Point (BEP): pump should be operated here to minimise vibration and cavitation risk",
        "Brayton cycle: compressor (1→2), combustor (2→3), turbine (3→4); η_th = 1 − T₁/T₂ (ideal)",
        "Hydraulic turbines: Pelton (impulse, high head), Francis (reaction mixed, medium), Kaplan (reaction axial, low head)",
        "Cavitation: local p < p_vapour → vapour bubbles → collapse → erosion; prevent with adequate NPSH_A > NPSH_R",
        "Compressor stall/surge: boundary layer separation on blade suction surface at low flow — must be avoided",
      ]}/>

      <h2 style={S.h2}>Classification of Turbomachines</h2>
      <p style={S.p}>
        Every turbomachine can be classified along two independent axes: by the direction of energy exchange (work-absorbing or work-producing), and by the geometry of the flow path through the impeller (axial, mixed, or radial). A third classification distinguishes impulse machines (where all pressure change occurs in stationary nozzles and the rotor operates at approximately constant pressure) from reaction machines (where pressure changes both in the stator and across the rotor itself). The specific speed Ns ties all these classifications together, providing a single dimensionless parameter that dictates the optimal geometry for any given combination of head, flow rate, and rotational speed.
      </p>
      <DiagramClassification/>
      <Table2 rows={[
        ["Work-absorbing machines","Pumps (liquids), fans (gas, low Δp), blowers (gas, medium Δp), compressors (gas, high Δp)\nAll increase fluid total enthalpy: h₀₂ > h₀₁"],
        ["Work-producing machines","Hydraulic turbines (water), steam turbines (steam), gas turbines (combustion gas)\nAll decrease fluid total enthalpy: h₀₂ < h₀₁"],
        ["Axial machines","Flow direction ∥ shaft axis;  high Ns, high Q, low H/Δp\nExamples: axial fan, aircraft jet compressor/turbine, Kaplan turbine, ship propeller"],
        ["Radial (centrifugal) machines","Flow direction ⊥ shaft axis (radially outward for pumps/compressors)\nLow Ns, low Q, high H/Δp;  examples: centrifugal pump, centrifugal compressor, Pelton wheel"],
        ["Mixed-flow machines","Flow has both axial and radial components;  intermediate Ns\nExamples: mixed-flow pump, Francis turbine"],
        ["Impulse turbine","All pressure drop in nozzles (stator);  rotor at constant pressure (p_atm for Pelton)\nNo pressure change across rotor — only velocity change"],
        ["Reaction turbine","Pressure drop split between stator and rotor;  rotor has varying static pressure\nGenerates lift on blade profiles (like an aerofoil) — higher power density"],
        ["Degree of reaction R","R = static enthalpy rise in rotor / total stage enthalpy rise\nR = 0: pure impulse;  R = 0.5: 50% reaction (commonest axial stage);  R = 1: pure reaction"],
      ]}/>

      <h2 style={S.h2}>The Euler Turbomachine Equation and Velocity Triangles</h2>
      <p style={S.p}>
        The Euler turbomachine equation is the single most important result in turbomachinery theory. Derived purely from Newton's second law applied to angular momentum (moment of momentum), it states that the specific work exchanged between a rotor and a fluid equals the change in the product of blade speed U and the whirl (circumferential) component of absolute velocity Cu. Critically, it makes no assumptions about the fluid (compressible or incompressible, viscous or inviscid) and applies equally to pumps, compressors, fans, and all turbines.
      </p>
      <DiagramVelocityTriangles/>
      <Table2 rows={[
        ["Absolute velocity C","Velocity seen by a stationary observer\nVector sum: C = U + W"],
        ["Blade speed U","Circumferential speed of the blade: U = ω r\nVaries with radius — important in radial machines where r₂ ≫ r₁"],
        ["Relative velocity W","Velocity seen by an observer riding on the blade\nW = C − U;  flow relative to blade must align with blade angle at design point"],
        ["Whirl component Cu","Tangential (circumferential) component of absolute velocity\nCu = C sinα;  this is what appears in the Euler equation"],
        ["Axial component Ca","Axial (meridional) component of absolute velocity\nCa = C cosα;  determines flow rate: Q = A · Ca"],
        ["Euler work (specific)","W = U₂Cu₂ − U₁Cu₁  (J/kg)\nPositive = work input to fluid (pump);  negative = work output from fluid (turbine)"],
        ["Euler head","H_Euler = (U₂Cu₂ − U₁Cu₁)/g  (m of fluid)\nUpper bound on actual pump head — real head lower due to recirculation and friction"],
        ["Incidence angle","Difference between flow angle and blade angle at leading edge\nOff-design operation → incidence losses;  design point: zero incidence"],
        ["Slip factor σ","σ = Cu₂_actual / Cu₂_theoretical  (< 1 for real impellers)\nDue to finite blade count and blade curvature;  σ ≈ 0.85–0.95 typical"],
      ]}/>

      <h2 style={S.h2}>Centrifugal Pumps — Design and Performance</h2>
      <p style={S.p}>
        The centrifugal pump is the most widely used turbomachine in the world — responsible for more than 20% of global industrial electricity consumption. Fluid enters axially at the eye of the impeller, is accelerated radially outward by the rotating vanes, and exits into the volute casing where kinetic energy is converted to pressure. The performance is fully described by three curves plotted against flow rate Q at a fixed rotational speed: the head-flow (H-Q) curve, the efficiency curve, and the shaft power curve.
      </p>
      <DiagramCentrifugalPump/>
      <Table2 rows={[
        ["Theoretical (Euler) head","H_th = (U₂Cu₂ − U₁Cu₁)/g\nWith no pre-swirl (Cu₁=0): H_th = U₂Cu₂/g = U₂(U₂−Ca₂/tanβ₂)/g"],
        ["Backward-curved blades","β₂ < 90°: H decreases with Q → stable characteristic (preferred)\nMost centrifugal pumps use backward-swept blades (β₂ ≈ 15–35°)"],
        ["Forward-curved blades","β₂ > 90°: H increases with Q → potentially unstable\nUsed in forward-curved fans; high head at high flow"],
        ["Radial blades","β₂ = 90°: H roughly constant with Q\nUsed in some low-cost fans; susceptible to overloading"],
        ["Volute casing","Spiral passage of increasing cross-section surrounding impeller\nConverts velocity head to pressure (diffuser action);  tongue separates inlet from outlet"],
        ["Shut-off head H₀","Head at Q = 0: H₀ = U₂²/g × σ × (1 − sin β₂)  (approx.)\nMaximum pressure pump can produce — do not run against closed valve for long"],
        ["Overall efficiency","η = ρgQH / P_shaft\nProduct of hydraulic, volumetric, and mechanical efficiencies\nη_max ≈ 70–92% depending on size and specific speed"],
        ["Minimum flow","Operating well below BEP causes recirculation, temperature rise, cavitation\nMinimum flow ≈ 25–40% of BEP flow for most pumps"],
      ]}/>

      <h2 style={S.h2}>Axial Flow Compressors — Stages and Stall</h2>
      <p style={S.p}>
        The multi-stage axial compressor is the heart of every modern gas turbine engine, responsible for raising inlet air pressure from ~1 atm to 20–50 atm across 10–20 stages. Each stage consists of a rotor row (which does work on the gas and raises total enthalpy) followed by a stator row (which converts the swirl kinetic energy to static pressure). The aerodynamics of each blade row is essentially that of aerofoil sections operating in a high-speed, highly constrained duct environment.
      </p>
      <DiagramAxialCompressor/>
      <Table2 rows={[
        ["Stage pressure ratio","Typical single-stage: 1.1–1.4 (subsonic); up to 2.0 (transonic fan)\nOverall: (1.3)^12 ≈ 23 — requires many carefully designed stages"],
        ["Flow coefficient φ","φ = Ca/U  (axial velocity / blade speed)\nSmall φ → low flow for given size;  design point φ ≈ 0.4–0.7"],
        ["Stage loading ψ","ψ = ΔW/U² = ΔCu/U  (stage work / blade speed²)\nDesign range ψ ≈ 0.3–0.4;  higher ψ → more work per stage but less stall margin"],
        ["Degree of reaction","R = (C₂² − C₁²)/(2ΔW);  50% reaction most common (equal work in rotor and stator)\nHigh reaction → more pressure rise in rotor (fewer stages but more stall-prone)"],
        ["Compressor stall","Blade boundary layer separates when angle of attack too high (low flow)\nIndividual blade stall → rotating stall → full annular surge"],
        ["Rotating stall","Stall cells rotate around annulus at ~50% rotor speed\nReduced performance;  can progress to full surge"],
        ["Surge","Complete flow breakdown — reversal of flow through compressor\nViolent pressure oscillations;  destructive if sustained\nPrevented by variable stator vanes (VSV) and bleed valves"],
        ["Isentropic efficiency η_is","η_is = (h₂s − h₁)/(h₂ − h₁) = (T₂s − T₁)/(T₂ − T₁)\nTypically 88–92% per stage;  polytropic efficiency useful for multi-stage comparison"],
        ["Transonic blade","Modern front stages operate at relative Mach > 1 → shock losses\nShock + boundary layer interaction limits maximum pressure ratio per stage"],
      ]}/>

      <h2 style={S.h2}>Dimensional Analysis and Similarity Laws</h2>
      <p style={S.p}>
        The Buckingham π theorem applied to turbomachinery yields a small set of dimensionless groups that completely characterise machine performance regardless of size or speed. These groups allow test results from model machines to be applied to full-scale designs, and they define the families of geometrically similar machines. The most important derived parameter is specific speed, which maps directly to optimal geometry.
      </p>
      <DiagramSimilarityLaws/>
      <Table2 rows={[
        ["Flow coefficient CQ","CQ = Q/(ND³)\nDimensionless flow rate;  fixes operating point on performance curve"],
        ["Head coefficient CH","CH = gH/(N²D²)\nDimensionless head;  analogous to pressure coefficient"],
        ["Power coefficient CP","CP = P/(ρN³D⁵)\nDimensionless shaft power;  CP = CH × CQ / η"],
        ["Specific speed Ns","Ns = N Q^(1/2) / (gH)^(3/4)  (dimensionless)\nEquivalent dimensional (SI): Ns,dim = N(rpm)·Q(m³/s)^(1/2)/H(m)^(3/4)"],
        ["Specific diameter Ds","Ds = D(gH)^(1/4) / Q^(1/2)\nUsed with Ns in Cordier diagram to select optimal D for a design point"],
        ["Cordier diagram","Plot of Ns vs Ds for best-efficiency machines of all types\nDefines the locus of optimal design — turbomachines near this line are efficient"],
        ["Scaling laws (geometrically similar)","Q₂/Q₁ = (N₂/N₁)(D₂/D₁)³\nH₂/H₁ = (N₂/N₁)²(D₂/D₁)²\nP₂/P₁ = (ρ₂/ρ₁)(N₂/N₁)³(D₂/D₁)⁵"],
        ["Reynolds number effect","Above Re ~ 10⁶ (turbulent BL): efficiency nearly independent of Re\nSmall models at lower Re slightly underpredict full-scale η — correction applied"],
        ["Mach number effect (compressors)","If tip Mach Ma_tip > 0.9: compressibility alters performance\nPressure coefficient CH includes γ for gases; separate compressible similarity groups needed"],
      ]}/>

      <h2 style={S.h2}>Gas Turbine Engines — The Brayton Cycle</h2>
      <p style={S.p}>
        The gas turbine engine combines a compressor, combustion chamber, and turbine in series on a common shaft (plus a power turbine or propelling nozzle). The thermodynamic cycle is the Brayton cycle. In a jet engine, the turbine extracts only enough work to drive the compressor — the remaining enthalpy is exhausted as a high-speed jet for thrust. In a power gas turbine, additional turbine stages extract work to drive an electrical generator. The Turbine Inlet Temperature (TIT) is the single most important design parameter: doubling TIT approximately doubles specific power output.
      </p>
      <DiagramBraytonCycle/>
      <Table2 rows={[
        ["Ideal Brayton efficiency","η_th = 1 − T₁/T₂ = 1 − (p₁/p₂)^((γ−1)/γ)\nFor pressure ratio 20 and γ=1.4: η_th ≈ 57% (ideal)"],
        ["Turbine Inlet Temperature","TIT (T₃) limited by blade material: ~1200°C (uncooled), ~1700°C (film-cooled superalloy)\nTIT is the most critical thermodynamic parameter — higher TIT → more work and efficiency"],
        ["Back-work ratio","BWR = W_compressor / W_turbine ≈ 40–70%\nMuch higher than steam cycles (~1%) — motivates intercooling and regeneration"],
        ["Turbine blade cooling","Air bled from compressor passes through internal channels and film holes\nAllows TIT > T_blade_melting — complex aerothermal design"],
        ["Polytropic efficiency","η_p = (γ−1)/γ × ln(p₂/p₁)/ln(T₂/T₁)\nStage efficiency appropriate for multi-stage machines;  accounts for preheat effect"],
        ["Turbofan bypass ratio","Modern turbofans: BPR ≈ 6–12;  ultra-high BPR ≈ 12–15 (GE9X)\nHigh BPR → lower jet velocity → higher propulsive efficiency at high subsonic speeds"],
        ["Recuperator / regenerator","Heat exchanger recovering exhaust heat to preheat compressed air\nRaises cycle efficiency from ~35% to 45–50% for industrial power turbines"],
        ["Combined cycle","Gas turbine exhaust (550°C) drives a steam turbine below it\nOverall η up to 62–64% — highest thermal efficiency of any power cycle"],
      ]}/>

      <h2 style={S.h2}>Hydraulic Turbines</h2>
      <p style={S.p}>
        Hydroelectric power plants use hydraulic turbines to convert the potential energy of elevated water into shaft work. Three main types cover the full range of head and flow combinations encountered in practice. Selection is based on specific speed: high-head, low-flow sites use Pelton wheels; medium-head sites use Francis turbines; low-head, high-flow sites use Kaplan turbines. All are reaction turbines except the Pelton, which operates at atmospheric pressure throughout the runner.
      </p>
      <DiagramHydraulicTurbines/>
      <Table2 rows={[
        ["Available power","P_available = ρgQH\nH = net head (reservoir to tailwater less pipe friction losses)"],
        ["Turbine efficiency","η_turbine = P_shaft / (ρgQH)\nBest hydraulic turbines: η ≈ 90–95% — among the highest of all energy converters"],
        ["Pelton wheel","All pressure drop in nozzle: V_jet = Cv√(2gH)\nOptimal: U = V_jet/2  (bucket speed = half jet speed)\nη_max when U/V_j ≈ 0.46 (accounting for bucket friction)"],
        ["Francis turbine","Spiral casing → stay vanes → guide vanes (adjustable) → runner → draft tube\nWide operating range;  guide vane angle adjusted to maintain η across flow variation"],
        ["Kaplan turbine","Adjustable runner blades + adjustable guide vanes (double regulation)\nMaintains high η over very wide Q range — essential for tidal and run-of-river plants"],
        ["Draft tube","Diverging tube from runner exit to tailwater level\nRecoveries runner exit kinetic energy; creates negative pressure (suction) at runner exit\nIncreases effective net head by V_exit²/(2g) − losses"],
        ["Specific speed (turbines)","Ns = N·P^(1/2) / (ρ^(1/2)·(gH)^(5/4))\nPelton Ns ≈ 0.05–0.4;  Francis Ns ≈ 0.4–2.0;  Kaplan Ns ≈ 2.0–5.0  (SI dimensionless)"],
        ["Runaway speed","Speed turbine reaches if load suddenly disconnected (governor fails)\nPelton: ~180% normal speed;  Kaplan: up to 300% — structural design must withstand it"],
      ]}/>

      <h2 style={S.h2}>Pump and Compressor System Operation</h2>
      <p style={S.p}>
        A pump or compressor always operates at the intersection of its performance curve and the system resistance curve. Understanding this intersection — and how it moves with flow demand, pipe ageing, or speed changes — is essential for energy-efficient system design. The affinity laws govern how performance scales with speed, providing the theoretical basis for variable-speed drives (VSDs), which can dramatically reduce energy consumption in variable-demand systems.
      </p>
      <DiagramAffinityLaws/>
      <Table2 rows={[
        ["System curve","H_system = H_static + K·Q²\nH_static = elevation difference + fixed pressure difference;  K depends on pipe friction, valves, fittings"],
        ["Operating point","Intersection of pump H-Q curve and system curve\nMust lie close to BEP for efficient, reliable operation"],
        ["Variable speed drive (VSD)","Affinity laws: Q ∝ N, H ∝ N², P ∝ N³\nReducing speed from 100% to 80%: power reduced to (0.8)³ = 51.2% — enormous energy saving"],
        ["Series pumping","Two pumps in series: H_combined = H₁ + H₂ at same Q\nUsed when system head too high for one pump;  both pumps run at same flow rate"],
        ["Parallel pumping","Two pumps in parallel: Q_combined = Q₁ + Q₂ at same H\nUsed when flow too high for one pump;  works best when pumps are identical"],
        ["Pump selection rule","Select pump so BEP ≈ 85–110% of normal operating Q\nOperating far from BEP: increased cavitation risk, bearing loads, impeller wear"],
        ["Throttle vs speed control","Throttle (control valve): wastes energy as pressure drop across valve\nSpeed control (VSD): reduces impeller work — far more efficient for Q reduction"],
        ["Unstable curve region","Some pumps: H increases as Q decreases to zero — could cause hunting\nPrefer pumps with monotonically falling H-Q curves for stable control"],
      ]}/>

      <h2 style={S.h2}>Cavitation and NPSH</h2>
      <p style={S.p}>
        Cavitation is the most insidious failure mode in liquid turbomachinery. When the local static pressure anywhere in the flow path drops below the vapour pressure of the liquid, vapour bubbles form. These bubbles are then swept into higher-pressure regions where they collapse implosively — generating localised pressures estimated at 1–10 GPa and temperatures of several thousand Kelvin. Even brief exposure causes severe erosion of impeller metal, and the filling of flow passages with vapour dramatically reduces pump performance (head breakdown).
      </p>
      <DiagramCavitation/>
      <Table2 rows={[
        ["Vapour pressure","p_v(water): 0.61 kPa at 0°C;  2.34 kPa at 20°C;  47.4 kPa at 80°C;  101.3 kPa at 100°C\nCavitation more likely at higher water temperature"],
        ["NPSH available","NPSH_A = p_s/(ρg) + V_s²/(2g) − p_v/(ρg)\nCalculated for the system — depends on pump installation, pipe diameter, fluid temperature"],
        ["NPSH required","NPSH_R: minimum NPSH at which pump maintains ≥ 97% of non-cavitating head at given Q\nProvided by manufacturer from testing — increases with Q³"],
        ["Design criterion","NPSH_A ≥ NPSH_R + safety margin (0.5–1 m typical)\nFailure to satisfy this criterion guarantees cavitation damage"],
        ["Thoma cavitation number","σ = NPSH_A / H_total\nCritical σ_c is a pump design parameter;  operate at σ > σ_c"],
        ["Suction specific speed","Nss = N Q^(1/2) / (g·NPSH_R)^(3/4)\nNss < 3.0 (SI dimensionless): low cavitation risk;  Nss > 4.0: careful design needed"],
        ["Avoiding cavitation","Reduce suction lift;  increase suction pipe diameter (lower V_s);  lower water temperature\nUse double-suction impeller (halves Q per side);  reduce speed;  use inducer"],
        ["Inception vs damage","Cavitation inception: first detectable bubbles (NPSH = NPSH_i)\nHead breakdown: 3% head loss criterion (NPSH_R defined here)\nExtensive damage at still lower NPSH — avoid even incipient cavitation in service"],
      ]}/>

      <h2 style={S.h2}>Steam Turbines</h2>
      <Table2 rows={[
        ["Rankine cycle","Boiler (evaporation + superheat) → turbine → condenser → pump → boiler\nSteam turbine expands high-pressure steam; exhaust condensed to liquid for pumping"],
        ["Impulse stage (de Laval)","All pressure drop in nozzle;  rotor receives high-velocity jet at constant pressure\nSimple, single-stage;  used in small and high-speed turbines"],
        ["Reaction stage (Parsons)","50% reaction: equal pressure drop in stator and rotor\nUsed in most multi-stage power turbines;  more stages but lower stresses"],
        ["Curtis stage","Two-row velocity-compounded impulse stage\nAbsorbs large velocity in one stage — used as first stage of impulse turbines"],
        ["Reheat cycle","After HP turbine, steam returned to boiler for reheating before LP turbine\nImproves η by ~5% and raises exit quality (reduces blade erosion)"],
        ["Regenerative feedheating","Bleed steam from turbine heats feedwater in heat exchangers\nRaises cycle η by up to 10% — used in all modern power stations"],
        ["Condenser vacuum","Low condenser pressure (0.003–0.010 bar abs.) raises enthalpy drop across turbine\nCooling water temperature and flow rate set the achievable vacuum"],
        ["Moisture erosion","Wet steam droplets erode LP turbine blades (last stages)\nMoisture fraction limited to <12%;  stellite hard-facing or moisture extractors used"],
        ["Supercritical steam","Modern plants: 25–30 MPa, 600°C — beyond critical point of water (22.1 MPa, 374°C)\nRankine η ≈ 46–48%;  ultra-supercritical ≈ 47–50%"],
      ]}/>

      <h2 style={S.h2}>Engineering Applications</h2>
      <Table2 rows={[
        ["Water supply","Centrifugal pumps in series to overcome 10–100 m city distribution heads\nVariable-speed drives match output to demand — 20–40% energy saving vs throttle control"],
        ["Jet engine (turbofan)","LP compressor (fan) + HP compressor + combustor + HP turbine + LP turbine + nozzle\nBPR 10–15 for modern engines (GEnx, PW1000G, LEAP); specific fuel consumption < 0.55 kg/kN·h"],
        ["Gas turbine power plant","Simple cycle η ≈ 38–42%;  combined cycle η ≈ 60–64%\nGT24/GT26 (ABB), GE H-class: > 400 MW, η_combined > 63%"],
        ["Hydroelectric","Itaipu: 14 GW, 20 Francis turbines, H = 118 m\nThree Gorges: 22.5 GW — world's largest power station"],
        ["Refrigeration / heat pump","Centrifugal or screw compressor raises refrigerant pressure\nCOP_refrigerator = Q_cold/(W_net);  COP_heat pump = Q_hot/W_net = COP_ref + 1"],
        ["Wind turbine","Horizontal-axis propeller (axial turbine) — power = ½ρAV³ × Cp\nBetz limit: Cp_max = 16/27 ≈ 0.593;  modern turbines Cp ≈ 0.45–0.50"],
        ["Turbocharger","Exhaust-driven centrifugal compressor boosts engine intake pressure\nTurbine and compressor on same shaft — no auxiliary power needed"],
        ["Cryogenic turbopumps","Space Shuttle SSME: LOX/LH2 turbopumps at 37,000 rpm, 276 atm outlet\nAmong the highest specific work turbomachines ever built"],
      ]}/>

      <InfoBox title="Key Formulae Summary" items={[
        "Euler work:  W = U₂Cu₂ − U₁Cu₁  (J/kg);  H_Euler = W/g  (m)",
        "Blade speed:  U = ωr = πDN/60  (m/s)",
        "Specific speed:  Ns = N·Q^(1/2) / (gH)^(3/4)  (dimensionless)",
        "Affinity laws:  Q₂/Q₁ = N₂/N₁;  H₂/H₁ = (N₂/N₁)²;  P₂/P₁ = (N₂/N₁)³",
        "Pump overall efficiency:  η = ρgQH / P_shaft",
        "Isentropic efficiency (compressor):  η_is = (h₂s−h₁)/(h₂−h₁) = (T₂s−T₁)/(T₂−T₁)",
        "Ideal Brayton efficiency:  η_th = 1 − T₁/T₂ = 1 − (1/r)^((γ−1)/γ)  (r = pressure ratio)",
        "NPSH available:  NPSH_A = p_s/(ρg) + V_s²/(2g) − p_v/(ρg)",
        "Hydraulic turbine power:  P = η·ρgQH",
        "Pelton optimal blade speed:  U = V_jet/2;  V_jet = Cv√(2gH)",
        "Stage loading coefficient:  ψ = ΔW/U² = ΔCu/U  (axial compressor)",
      ]}/>
    </div>
  );
}